/**
 * Centralized simulation parameters. Modules import only the sections they need.
 */

import { DEG2RAD } from '../utils.js';

// ── Scenarios ─────────────────────────────────────────────────────────────
// Menu metadata (category / mission / code / desc / est) drives the start
// screen's Category → Mission browser; category order comes from
// SCENARIO_CATEGORIES. `image` is an optional detail-card header, defaulting to
// '/assets/ui/mission_placeholder.jpg'.
export const SCENARIOS = [
  {
    name: 'Basic Aircraft Handling',
    category: 'Flight School',
    mission: 'Basic Aircraft Handling',
    code: 'FT-101',
    desc: 'Perform a complete cold start, taxi procedures, runway departure, traffic pattern operations, and full-stop landing.',
    est: '10 MIN',
    x: -8985,
    z: 3466,
    altitude: 73,
    velocity: 0,
    heading: 45,
    randomAngle: 0,
    startEngineOn: false,
    gearUp: false,
    image: "/assets/ui/mission_training_basic.jpg",
    // Guided training run — key into TRAINING_MISSIONS (js/training/missions).
    // App builds a TrainingMission for any scenario carrying this field.
    training: 'basicHandling',
    // Fixed loadout: training scenarios fly clean, so the start screen hides the
    // Armament section and launches with this instead of the player's selection.
    loadout: { gau8: 'none', unlimited: false, flares: 'none', unlimitedFlares: false, aim9: 0, agm65: 0 },
  },
  {
    name: 'Close Air Support',
    category: 'Flight School',
    mission: 'Close Air Support',
    code: 'FT-102',
    desc: 'Employ the GAU-8/A cannon and the AGM-65 Maverick against ground targets: talked-through strafing attacks with a tactical break, then a standoff TV-guided strike on a disused airstrip.',
    est: '15 MIN',
    // Airborne start, as a combat sortie would be, but pinned rather than
    // drawn from COMBAT_SPAWNS: the lesson's pacing depends on the length of
    // the run-in. 19 km on heading 225 (south-east) puts the first gun target
    // dead on the nose with a little over two minutes to brief it — see
    // js/training/missions/closeAirSupport.js for the rest of the geometry.
    x: -5000,
    z: 2000,
    altitude: 900,
    velocity: 154,
    heading: 225,
    randomAngle: 0,
    startEngineOn: true,
    gearUp: true,
    image: "/assets/ui/mission_training_cas.jpg",
    training: 'closeAirSupport',
    // Unlike FT-101 this one is armed, but the loadout is still pinned (the
    // start screen hides the Armament section for any scenario carrying one).
    // Unlimited 30 mm and four Mavericks for two targets: a training run must
    // not become unwinnable because the student sprayed the drum or missed a
    // shot.
    loadout: { gau8: 'full', unlimited: true, flares: 'none', unlimitedFlares: false, aim9: 0, agm65: 4 },
  },
  {
    name: 'Air-to-Air Engagement',
    category: 'Flight School',
    mission: 'Air-to-Air Engagement',
    code: 'FT-103',
    desc: 'Employ the GAU-8/A and the AIM-9M against airborne targets: air-to-air gunnery with the funnel sight, then a Sidewinder engagement on the seeker tone.',
    est: '12 MIN',
    // Airborne start 15 km south-west of the first contact on heading 295, so
    // it sits on the nose with the whole gunnery brief still to run — see
    // js/training/missions/airToAir.js for the rest of the geometry.
    x: -11600,
    z: 15340,
    altitude: 900,
    velocity: 154,
    heading: 295,
    randomAngle: 0,
    startEngineOn: true,
    gearUp: true,
    image: "/assets/ui/mission_training_air.jpg",
    training: 'airToAir',
    // Two Sidewinders for one Hip. The rack is deliberately not generous the
    // way FT-102's Maverick load is: an AIM-9 against a Mi-8 that neither
    // manoeuvres nor flares effectively cannot miss, and if both are somehow
    // wasted the kill objective still accepts a gun kill.
    loadout: { gau8: 'full', unlimited: true, flares: 'none', unlimitedFlares: false, aim9: 4, agm65: 0 },
  },
  {
    name: 'Airborne',
    category: 'Free Flight',
    mission: 'Airborne',
    code: 'FF-001',
    desc: 'Start at 8000 feet.',
    est: 'NO LIMIT',
    x: 20000,
    z: -10000,
    altitude: 2438,
    velocity: 154,
    heading: 135,
    randomAngle: 0,
    startEngineOn: true,
    gearUp: true,
    image: "/assets/ui/mission_freeflight_airborne.jpg"
  },
  {
    name: 'Runway Takeoff',
    category: 'Free Flight',
    mission: 'Runway Takeoff',
    code: 'FF-002',
    desc: 'Take off from runway 35.',
    est: 'NO LIMIT',
    x: -9194.75,
    z: 3390,
    altitude: 73,
    velocity: 0,
    heading: -10,
    randomAngle: 0,
    startEngineOn: true,
    gearUp: false,
    image: "/assets/ui/mission_freeflight_runway.jpg"
  },
  {
    name: 'Landing Practice',
    category: 'Free Flight',
    mission: 'Landing Practice',
    code: 'FF-003',
    desc: 'Practice landing on runway 35 from the downwind leg.',
    est: 'NO LIMIT',
    x: -11600,
    z: 3390,
    altitude: 457,
    velocity: 102,
    heading: 170,
    randomAngle: 0,
    startEngineOn: true,
    gearUp: true,
    image: "/assets/ui/mission_freeflight_landing.jpg"
  },
  {
    name: 'Taxi from Parking',
    category: 'Free Flight',
    mission: 'Parking',
    code: 'FF-004',
    desc: 'Taxi from parking to runway 35.',
    est: 'NO LIMIT',
    x: -8985,
    z: 3466,
    altitude: 73,
    velocity: 0,
    heading: 45,
    randomAngle: 0,
    startEngineOn: true,
    gearUp: false,
    image: "/assets/ui/mission_freeflight_parking.jpg"
  },
  {
    name: 'Taxi from Parking (Cold Start)',
    category: 'Free Flight',
    mission: 'Parking (Cold Start)',
    code: 'FF-005',
    desc: 'Start the engines and taxi from parking to runway 35.',
    est: 'NO LIMIT',
    x: -8985,
    z: 3466,
    altitude: 73,
    velocity: 0,
    heading: 45,
    randomAngle: 0,
    startEngineOn: false,
    gearUp: false,
    image: "/assets/ui/mission_freeflight_parking_cold.jpg"
  },
  // ── Combat missions ──────────────────────────────────────────────────────
  // Terrain spans X∈[-35982,35982], Z∈[-23988,23988] (West=-X, North=-Z). A
  // ridge runs from (0,-23988) to the SE corner along x ≈ 0.75·(z+23988); ground
  // spawns sit east of it, Doorstep alone excepted. Enemy jets only engage when
  // spawned with `state: 'attackPlayer'`; otherwise they fly random waypoints.
  // There is no spawn timer — "late" MiGs are placed far off the east edge and
  // take time to close at ~540 m/s.
  //
  // Ground units carry `useRoads: true`, so a spawn's x/z only has to name the
  // right stretch of country — the road network does the rest, which is also
  // what keeps everything off the slopes. Armour is snapped onto the nearest
  // road and drives the network from there; the stationary Tunguskas have no
  // driver, so they are emplaced a couple of hundred feet off the roadside
  // instead, covering the route without standing in it. Because placement is
  // re-anchored either way, spawn discs can be generous without risking the
  // ridge. The bands below are where the baked graph actually has road east of
  // the ridge, and missions are spread across them so the fight isn't always in
  // the same corner of the map:
  //
  //   far north     z −19k…−17k   x  9k…25k
  //   northeast     z −17k…−10k   x  9k…28k   (densest part of the network)
  //   east central  z −10k… −2k   x 19k…28k
  //   east          z  −2k…  5k   x 22k…27k
  //   southeast     z   5k… 12k   x 24k…30k
  //
  // Beyond z ≈ 12k the network has almost nothing east of the ridge, so nothing
  // is placed there. `heading` picks which way along the road a column sets off
  // (compass-CCW: 0 = N, 90 = W, 180 = S, 270 = E) — westward for an advance on
  // the field. Junctions after that are random, so a column wanders a few km
  // over a mission rather than holding one stretch.
  //
  // Containment caveat: that wandering is not bounded. The graph is one
  // connected 175 km component whose southern arm crosses west of the ridge, so
  // a long mission gives a southeast column time to find it — measured at
  // roughly 1 run in 8, usually about 1.4 km past the line. Starting those
  // columns *westward* (heading 90) is what keeps it that rare; sent east they
  // pick the southern arm up 5 times in 8 and can end 10 km over. Placement is
  // the only lever available here: properly bounding it would need the driver
  // to weight its junction choices, which it deliberately does not do.
  {
    name: 'Doorstep',
    category: 'Combat',
    mission: '01 Doorstep',
    code: 'CB-001',
    desc: 'Armored scouts have rolled up to within a few miles of the field. Warm up the gun and see them off.',
    est: '5 MIN',
    spawns: [
      // The one mission deliberately placed on the home (west) side of the
      // ridge, and so the one armour spawn with no `useRoads`: the road network
      // is entirely east of the mountains, and snapping would drag these three
      // some 25 km away from the field they are supposed to be threatening.
      { type: 'btr80', x: 4000, y: 'agl:0', z: 3500, heading: 95, initialHealth: 100, count: 3, spawnRadius: 3000 },
    ],
    image: "/assets/ui/mission_combat_01.jpg"
  },
  {
    name: 'Steel & Rotors',
    category: 'Combat',
    mission: '02 Steel & Rotors',
    code: 'CB-002',
    desc: 'A pair of armored columns is pushing west of the ridge, with rotary support working the same valley. Break up the advance.',
    est: '10 MIN',
    // East central — the corridor between the northeast cluster and the eastern
    // roads, so the second mission already reads as somewhere other than the
    // northeast every later mission used to share.
    spawns: [
      { type: 'btr80', x: 19300, y: 'agl:0', z: -8300, heading: 90, initialHealth: 100, count: 2, spawnRadius: 2000, useRoads: true },
      { type: 'btr80', x: 22700, y: 'agl:0', z: -4800, heading: 90, initialHealth: 100, count: 2, spawnRadius: 2000, useRoads: true },
      // Mi-8 transports loitering nearby — soft targets. Not road-bound.
      { type: 'mi8', x: 21500, y: 'agl:250', z: -6500, heading: 90, initialHealth: 100, count: 2, spawnRadius: 4000 },
    ],
    image: "/assets/ui/mission_combat_02.jpg"
  },
  {
    name: 'Fast Mover',
    category: 'Combat',
    mission: '03 Fast Mover',
    code: 'CB-003',
    desc: 'Two helo flights are operating east of the mountains — and they did not come alone. Mind the fast mover.',
    est: '10 MIN',
    // Southeast. All-air, so nothing here is road-bound — it is placed south
    // purely so the mission list stops marching up and down one meridian.
    spawns: [
      { type: 'mi8', x: 26000, y: 'agl:250', z:  6000, heading: 90, initialHealth: 100, count: 2, spawnRadius: 3000 },
      { type: 'mi8', x: 28500, y: 'agl:300', z: 10000, heading: 90, initialHealth: 100, count: 2, spawnRadius: 3000 },
      // Lone MiG-21 covering the helos.
      { type: 'mig21', x: 30000, y: 'agl:1200', z: 8000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 2000 },
    ],
    image: "/assets/ui/mission_combat_03.jpg"
  },
  {
    name: 'Needle in a Haystack',
    category: 'Combat',
    mission: '04 Needle in a Haystack',
    code: 'CB-004',
    desc: 'A single radar SAM is hiding somewhere in the eastern flats. Stay low, find it, and destroy it before it ranges you.',
    est: '8 MIN',
    // The eastern roads, east central down into the southeast.
    spawns: [
      // Huge spawn disc — position changes every start, so it must be hunted
      // down; consecutive launches land a good 6 km apart on average. It can be
      // this wide precisely because `useRoads` re-anchors the result to the
      // roadside: wherever the jitter throws it, it ends up emplaced beside a
      // road on ground a vehicle could have driven to, instead of up the ridge.
      // Centred on z = 0 rather than down in the southeast because the road
      // network's southern arm crosses west of the ridge, and an 8 km disc
      // centred any further south starts anchoring to it.
      { type: 'tunguska', x: 28000, y: 'agl:0', z: 0, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 8000 },
    ],
    image: "/assets/ui/mission_combat_04.jpg"
  },
  {
    name: 'Iron Tide',
    category: 'Combat',
    mission: '05 Iron Tide',
    code: 'CB-005',
    desc: 'A massed armored push east of the ridge, with a mobile SAM somewhere in the column. Blunt it before it reaches the pass.',
    est: '15 MIN',
    // Northeast — the densest part of the road graph, which is what a massed
    // push wants: three columns on connecting roads that can converge or split
    // at junctions rather than three separate convoys on one track.
    spawns: [
      { type: 'btr80', x: 15400, y: 'agl:0', z: -17300, heading: 90, initialHealth: 100, count: 2, spawnRadius: 2000, useRoads: true },
      { type: 'btr80', x: 19500, y: 'agl:0', z: -14800, heading: 90, initialHealth: 100, count: 3, spawnRadius: 2000, useRoads: true },
      { type: 'btr80', x: 22500, y: 'agl:0', z: -11500, heading: 95, initialHealth: 100, count: 2, spawnRadius: 2000, useRoads: true },
      { type: 'tunguska', x: 20000, y: 'agl:0', z: -13500, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 2500 },
    ],
    image: "/assets/ui/mission_combat_05.jpg"
  },
  {
    name: 'Mixed Company',
    category: 'Combat',
    mission: '06 Mixed Company',
    code: 'CB-006',
    desc: 'Armor on the ground, a radar SAM in support, and a fighter on patrol. A little of everything east of the mountains.',
    est: '12 MIN',
    // East — the belt of road either side of z = 0, roughly level with the
    // field rather than north of it.
    spawns: [
      { type: 'btr80', x: 23400, y: 'agl:0', z:     0, heading: 90, initialHealth: 100, count: 2, spawnRadius: 2000, useRoads: true },
      { type: 'btr80', x: 24700, y: 'agl:0', z:  2200, heading: 100, initialHealth: 100, count: 2, spawnRadius: 2000, useRoads: true },
      { type: 'tunguska', x: 24000, y: 'agl:0', z: -1500, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 3500 },
      { type: 'mig21', x: 50000, y: 'agl:1500', z: 1000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 8000 },
    ],
    image: "/assets/ui/mission_combat_06.jpg"
  },
  {
    name: 'High Noon',
    category: 'Combat',
    mission: '07 High Noon',
    code: 'CB-007',
    desc: 'Two fighters are holding the far eastern edge, well apart. A guns-and-missiles duel with no ground clutter to hide in.',
    est: '10 MIN',
    spawns: [
      // Two MiGs on the east edge, ~10 miles apart in Z, engaging at once.
      { type: 'mig21', x: 33000, y: 'agl:2000', z: -12000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 5000 },
      { type: 'mig21', x: 40000, y: 'agl:2500', z:   8000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 5000 },
    ],
    image: "/assets/ui/mission_combat_07.jpg"
  },
  {
    name: 'Slow Burn',
    category: 'Combat',
    mission: '08 Slow Burn',
    code: 'CB-008',
    desc: 'A long armored column screened by two mobile SAMs. Work the ground methodically — but do not get comfortable; air cover is inbound.',
    est: '15 MIN',
    // Southeast — one long column with the two SAMs spread north and south of
    // it along the same stretch of country.
    spawns: [
      { type: 'btr80', x: 27300, y: 'agl:0', z: 6400, heading: 90, initialHealth: 100, count: 8, spawnRadius: 2000, useRoads: true },
      // Kept over road country rather than pushed to the far southeast corner:
      // out past x ≈ 30k there is almost no road left to be emplaced beside,
      // and the placement just gets dragged back west anyway.
      { type: 'tunguska', x: 25500, y: 'agl:0', z: 3000, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 3500 },
      { type: 'tunguska', x: 28500, y: 'agl:0', z: 7000, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 3000 },
      // Parked far east — a couple of minutes out, so the ground threat comes first.
      { type: 'mig21', x: 60000, y: 'agl:3000', z: 6000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 10000 },
    ],
    image: "/assets/ui/mission_combat_08.jpg"
  },
  {
    name: 'Gathering Storm',
    category: 'Combat',
    mission: '09 Gathering Storm',
    code: 'CB-009',
    desc: 'A full combined-arms push spread across the eastern flats — armor, mobile SAMs, and fighters trickling in from the east.',
    est: '20 MIN',
    // Genuinely spread out: far north, east central, and southeast at once, so
    // clearing it is a tour of the map rather than one orbit. The player has to
    // transit between three separate fights.
    spawns: [
      { type: 'btr80', x: 13200, y: 'agl:0', z: -19000, heading: 90, initialHealth: 100, count: 2, spawnRadius: 2000, useRoads: true },
      { type: 'btr80', x: 22500, y: 'agl:0', z: -11500, heading: 95, initialHealth: 100, count: 3, spawnRadius: 2000, useRoads: true },
      { type: 'btr80', x: 24600, y: 'agl:0', z:   4100, heading: 85, initialHealth: 100, count: 3, spawnRadius: 2000, useRoads: true },
      { type: 'tunguska', x: 18000, y: 'agl:0', z: -17000, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 4000 },
      { type: 'tunguska', x: 29000, y: 'agl:0', z:   6000, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 3500 },
      // Staggered by distance so the second arrives minutes after the first.
      { type: 'mig21', x:  60000, y: 'agl:2200', z: -13000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 10 },
      { type: 'mig21', x: 115000, y: 'agl:3000', z:  -6000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 10 },
    ],
    image: "/assets/ui/mission_combat_09.jpg"
  },
  {
    name: 'Main Event',
    category: 'Combat',
    mission: '10 Main Event',
    code: 'CB-010',
    desc: 'The works: three heavy armored columns spread the length of the map, a SAM belt, and a steady stream of fighters from the east. Bring everything.',
    est: '25 MIN',
    // Three heavy columns, one per end of the map — far north, east central,
    // southeast — with the SAM belt strung between them.
    spawns: [
      { type: 'btr80', x: 13300, y: 'agl:0', z: -18900, heading: 90, initialHealth: 100, count: 5, spawnRadius: 2000, useRoads: true },
      { type: 'btr80', x: 22700, y: 'agl:0', z:  -4900, heading: 95, initialHealth: 100, count: 6, spawnRadius: 2000, useRoads: true },
      // Started westward, and from the northern end of the southeast roads.
      // Both matter over a 25-minute mission: sent the other way this column
      // picks up the network's southern arm, which curves southwest across the
      // ridge — see the note on containment below.
      { type: 'btr80', x: 26800, y: 'agl:0', z:   5600, heading: 90, initialHealth: 100, count: 4, spawnRadius: 2000, useRoads: true },
      { type: 'tunguska', x: 16000, y: 'agl:0', z: -17500, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 3000 },
      { type: 'tunguska', x: 24000, y: 'agl:0', z:  -6000, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 3000 },
      { type: 'tunguska', x: 28500, y: 'agl:0', z:   5000, heading: 0, initialHealth: 100, useRoads: true, spawnRadius: 3000 },
      // Progressively farther out, so they join the fight minutes apart.
      { type: 'mig21', x:  40000, y: 'agl:2000', z: -15000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 8000 },
      { type: 'mig21', x:  90000, y: 'agl:2800', z:  -5000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 8000 },
      { type: 'mig21', x: 150000, y: 'agl:3200', z:   6000, heading: 90, initialHealth: 100, state: 'attackPlayer', spawnRadius: 8000 },
    ],
    image: "/assets/ui/mission_combat_10.jpg"
  },
];

// Category display order for the start-screen scenario browser. An empty
// category renders as a "coming soon" group.
export const SCENARIO_CATEGORIES = ['Flight School', 'Free Flight', 'Combat'];

// Player spawn presets offered by the start screen's "Start" dropdown (Combat
// category only). App._resolveScenario merges the chosen pose onto the scenario
// at launch, leaving its enemy `spawns` untouched. Parking/runway poses mirror
// the matching Free Flight scenarios; 'airborne' drops the player ~1 mile NE of
// runway 35 heading east. `id` is the key stored in the launch config, `label`
// the dropdown text.
export const COMBAT_SPAWNS = [
  {
    id: 'airborne', label: 'Airborne',
    x: -5000, z: 500, altitude: 700, velocity: 154, heading: 270,
    randomAngle: 0, startEngineOn: true, gearUp: true,
    // ± scatter applied per launch (App._resolveScenario) so the entry is never
    // identical: metres in X/Z, metres of altitude (≈500 ft), degrees of heading.
    jitter: { xz: 1000, altitude: 152.4, heading: 45 },
  },
  {
    id: 'runway', label: 'Runway',
    x: -9194.75, z: 3390, altitude: 73, velocity: 0, heading: -10,
    randomAngle: 0, startEngineOn: true, gearUp: false,
  },
  {
    id: 'parking', label: 'Parking',
    x: -8985, z: 3466, altitude: 73, velocity: 0, heading: 45,
    randomAngle: 0, startEngineOn: true, gearUp: false,
  },
  {
    id: 'parkingCold', label: 'Parking (Cold start)',
    x: -8985, z: 3466, altitude: 73, velocity: 0, heading: 45,
    randomAngle: 0, startEngineOn: false, gearUp: false,
  },
];

// Default spawn selected when the Combat category is first opened.
export const DEFAULT_COMBAT_SPAWN = COMBAT_SPAWNS[0].id;

// Live "beauty shot" behind the start screen — the A-10 parked cold and dark at
// the ramp. Not in the menu list; spawned with the `preview` flag (controls
// disabled, START camera).
export const START_SCENARIO = {
  name: 'Start Screen',
  x: -8985,
  z: 3466,
  altitude: 73,
  velocity: 0,
  heading: 45,
  randomAngle: 0,
  startEngineOn: false,
  gearUp: false,
};

// ── Terrain ───────────────────────────────────────────────────────────
export const TERRAIN = {
  // Grid geometry — 12 cols (1..12) × 8 rows (A..H), tiles laid out in XZ
  tileSize: 5997,               // meters per tile edge
  cols: 12,
  rows: 8,
  origin: [0, 0],               // world XZ of grid center

  // Heightmap encoding (white = heightBias + heightScale, black = heightBias)
  heightScale: 900,
  heightBias: 0,

  viewDistance: 40000,          // meters — beyond this, tiles are culled entirely

  // Distance → (geometry subdivisions, texture resolution). First ring whose
  // maxDist exceeds the tile's camera distance is selected.
  lodRings: [
    { maxDist:  6000, subdivisions: 256, textureRes: 1024 },
    { maxDist: 12000, subdivisions:  128, textureRes:  512 },
    { maxDist: 24000, subdivisions:  64, textureRes:  256 },
    { maxDist: Infinity, subdivisions: 32, textureRes: 256 },
  ],

  textureCacheSize: 64,         // streaming LRU cap, per map type (color, height)

  // Cascaded bump map — three tiling scales of assets/terrain/bump.jpg blended
  // in each tile's bump shader, picked by 3D aircraft → fragment distance.
  cascadedBump: {
    nearTile: 40,        // metres per bump tile at close range
    midTile: 200,        // …at medium range
    farTile: 2000,       // …at long range
    nearFadeStart: 200,  // m — near cascade starts fading out
    nearFadeEnd: 500,    // m — near cascade fully gone
    farFadeStart: 1600,  // m — far cascade starts fading in
    farFadeEnd: 5000,    // m — far cascade fully present
    scale: 1,            // bumpScale passed to MeshStandardMaterial
    colorBumpStrength: 1, // colour-map luminance contribution to bump (0–1)
    // Distance-based strength multiplier, independent of cascade selection.
    strengthNearDist: 50,    // m — full near boost up to here
    strengthMidDist:  150,   // m — baseline (1.0) by here
    strengthFarDist:  2000,  // m — fully attenuated by here
    strengthNear:     1.6,   // multiplier within strengthNearDist
    strengthMid:      1.0,   // multiplier at strengthMidDist
    strengthFar:      0.4,   // multiplier at/past strengthFarDist
    // Grazing-angle fade — counters the dFdx/dFdy + 1/fDet blow-up in
    // perturbNormalArb. Distance-gated, since the artifact only shows at range.
    grazingExp:       1.5,   // pow(saturate(dot(V, N)), grazingExp); 0 disables
    grazingNearDist:  100,   // m — no grazing fade within
    grazingFarDist:   300,   // m — full grazing fade past this
    sampleOffsetCap:  0.25,  // height-sample offset cap, as a fraction of nearTile
  },

  skirtDepth: 40,               // m below the surface — hides LOD-boundary cracks
  geometryCacheSize: 32,        // LRU of displaced geometries for tiles just out of view
  fadeInDuration: 0.3,          // seconds — fade-in when a tile first becomes visible

  // ── Terrain grading ───────────────────────────────────────────────────
  // Terrain-only colour/exposure controls injected into every terrain material
  // (LOD tiles + skirts, mega tile, placeholder). The ground is almost entirely
  // IBL-lit — its up-facing normal collects the whole sky hemisphere, while a
  // low sun only contributes `intensity · N·L` — so sun intensity barely moves
  // it, and environmentIntensity / exposure are scene-wide. These are the
  // identity defaults; each LIGHTING condition overrides them via its own
  // `terrain: { … }` block.
  grading: {
    brightness:    1.0,       // final multiplier on the terrain's reflected light
    sunBoost:      1.0,       // multiplier on the DIRECT (sun) diffuse term only
    ambientBoost:  1.0,       // multiplier on the INDIRECT term (HDR env + AmbientLight)
    tint:          0xffffff,  // multiplicative albedo tint, sRGB hex
    tintStrength:  0.0,       // 0 = albedo unchanged, 1 = fully multiplied by `tint`
    saturation:    1.0,       // saturation of the baked satellite colour, pre-tint
  },

  debugWireframe: false,
  debugColorByLod: false,
};

// ── Roads ─────────────────────────────────────────────────────────────────
// Drivable road graph baked from assets/terrain/roads.svg by tools/build-roads.mjs
// (`npm run build:roads`) and followed by AIDriver's `path` state. A spawn entry
// opts in with `useRoads: true`: it is placed exactly as any other spawn — x/z,
// `spawnRadius` jitter, player-relative offsets — and the resulting formation is
// then snapped onto the nearest road. Junctions are taken at random, so a column
// wanders the network rather than shuttling one fixed route. Without the flag,
// ground vehicles keep their cross-country random-waypoint wandering.
export const ROADS = {
  data: 'data/roads.json',        // under assets/ — loaded by AssetManager

  // How far off the road a stationary AAA piece is parked when its spawn sets
  // `useRoads`. It has no driver, so the flag can't mean "follow the road" for
  // it — it means "emplace covering the road", which wants the vehicle beside
  // the carriageway rather than sitting in it blocking the traffic it screens.
  // A few hundred feet: close enough to read as guarding the route, far enough
  // that a passing column doesn't drive through it.
  roadsideOffset: { min: 60, max: 140 },   // m

  // Snap distance past which a `useRoads` spawn logs a warning, measured on top
  // of the entry's own `spawnRadius` (jitter can carry a spawn a full radius
  // off the road its centre sits beside, and that is not a mistake). Snapping
  // is never refused — that's what the flag asks for — but a formation dragged
  // kilometres further than its own disc explains is almost always a spawn
  // placed outside the area the road network actually covers.
  snapWarnDistance: 2000,         // m, plus spawnRadius

  // ── Pure pursuit ────────────────────────────────────────────────────────
  // The driver steers at a point `lookahead` metres further along its route,
  // not at the nearest road point: aiming at the road itself makes a rate-
  // limited vehicle weave, since it can only correct after it has already
  // drifted. Scaled by speed (a fixed distance is twitchy when crawling and
  // sluggish at cruise) and clamped so neither extreme runs away.
  lookaheadSeconds: 3.0,          // s of travel to look ahead
  // The floor has to stay in proportion to the turning radius, which shrinks
  // with speed: a vehicle slowed to a third of cruise for a junction turns
  // inside ~6 m, and aiming 30 m ahead from there puts the target well past
  // the corner — it cuts straight across instead of tracking round.
  lookaheadMin: 12,               // m
  lookaheadMax: 80,               // m — ceiling, or long straights cut corners early

  // Per-frame drift correction. Route progress is integrated from speed, which
  // overruns slightly on every corner (the vehicle cuts inside the polyline and
  // covers less ground than its speed implies); re-projecting the true position
  // onto the route absorbs that. Deliberately a *local* search — a global
  // nearest-point query would teleport progress across the map wherever the
  // network passes near itself, which any interesting road layout does.
  reprojectWindow: 60,            // m — ± search band around current progress

  // ── Cornering ───────────────────────────────────────────────────────────
  // Speed is cut in proportion to how hard the vehicle is being asked to turn,
  // which covers sharp junction turns and dead-end U-turns with one rule. It
  // also makes the turns physically possible: turn radius is v/ω, so a BTR at
  // its 11.2 m/s cruise and 25°/s yaw cap needs 26 m of road width to come
  // about, but only 9 m once it has slowed to a third of that.
  cornerSlowAngle: 50 * DEG2RAD,  // heading error at which the full cut applies
  cornerMinSpeedFactor: 0.3,      // fraction of cruise speed at that error

  // Convoy speed, as a fraction of the vehicle's cruiseSpeed. This is what
  // gives followers the authority to keep station: a follower's speed is capped
  // at the vehicle's own maxSpeed, and btr80 has maxSpeed === cruiseSpeed, so a
  // column led at full cruise has *zero* reserve to close a gap. Every metre
  // lost rounding a corner is then permanent, and a follower left far enough
  // behind drives straight at a target well ahead of it — cutting the corner
  // and leaving the road, which the next vehicle then inherits as its trail.
  // Leading below cruise leaves that reserve, and a convoy moving slower than
  // a lone vehicle's top speed is what you'd expect anyway.
  cruiseFactor: 0.75,

  // Distance from a dead-end node at which a vehicle begins slowing for the
  // turnaround, rather than arriving at cruise and swinging wide off the road.
  turnaroundSlowRange: 60,        // m

  // How close a vehicle must already be to a road before it will join the
  // network on losing its leader. Every ground vehicle holds the graph, so
  // without this the hand-placed cross-country columns in existing missions
  // would set off for the nearest road — typically kilometres away — the moment
  // their lead vehicle was destroyed.
  rejoinMaxDistance: 80,          // m
};

// ── Out-of-bounds / return-to-combat ──────────────────────────────────────
// The XZ play area is the full terrain grid extent centred on TERRAIN.origin;
// altitude never triggers OOB. Flying outside starts a grace countdown; if it
// expires the player is forced back to the boundary, levelled out and trimmed
// for steady flight (App._forceReturnToBounds).
export const BOUNDS = {
  halfX: (TERRAIN.cols * TERRAIN.tileSize) / 2,   // ≈ 35982 m
  halfZ: (TERRAIN.rows * TERRAIN.tileSize) / 2,   // ≈ 23988 m
  timeout: 10,            // s — grace period before the forced return
  insetMargin: 300,       // m — pull-in from the edge, so it can't instantly re-trigger
  minClearance: 152.4,    // m (≈ 500 ft) — floor on AGL after a forced return
  resetSpeed: {           // m/s — clamp band for the post-return level-flight airspeed
    min: 120,
    max: 200,
  },
};

// ── Vegetation ────────────────────────────────────────────────────────────
// Procedural foliage driven by a splat map covering the full terrain grid.
// World XZ is divided into a sliding-window grid of cells around the aircraft;
// each cell hashes out candidate instances, and a candidate becomes a live
// billboard when splat density at its position exceeds its per-slot random
// threshold. Each asset reads one RGB channel, so one texture encodes up to
// three types. Billboards are yaw-aligned quads with an alphaTest cutout plus a
// screen-space dither fade over the view-distance band to hide pop-in.
export const VEGETATION = {
  splatMap: 'vegetation/splat.jpg', // covers TERRAIN grid extents
  cellSize: 64,                     // m — sliding-window cell pitch
  candidatesPerCell: 16,            // 4×4 jittered grid within each cell
  viewDistance: 2000,               // m radius around aircraft
  fadeStart: 1200,                  // m — alpha begins ramping down
  fadeEnd: 2000,                    // m — fully gone
  alphaTest: 0.85,                   // texture alpha cutout threshold
  opacity: 0.8,                    // 0..1 — material alpha multiplier
  // Large-scale organic variation on top of the splat: one 2D simplex field
  // sampled at world XZ drives both density and size, so patches read as
  // 'forested' (more, larger) or 'sparse'. Set all four multipliers to 1 to
  // disable.
  noise: {
    scale: 800,           // m — wavelength of patches (larger = bigger biomes)
    densityLow: 0.1,      // density multiplier where noise is at minimum
    densityHigh: 1.4,     // density multiplier where noise is at maximum
    sizeLow: 0.6,         // size multiplier where noise is at minimum
    sizeHigh: 1.4,        // size multiplier where noise is at maximum
    seed: 12345,          // deterministic seed for the noise field
  },
  assets: [
    {
      name: 'shrub',
      channel: 'r',                 // 'r' | 'g' | 'b' — splat channel for density
      texture: 'vegetation/shrub_01.png',
      sizeMin: 2.0,                 // m
      sizeMax: 4.0,                 // m
      yOffset: 0,               // m — sink base into ground to hide seam
      threshold: 0.05,              // skip slots where density·channel falls below this
      densityGamma: 1.0,            // density curve (>1 = sharper edges)
      densityScale: 1.0,            // multiplier on splat channel value
      maxInstances: 20000,          // pool capacity for this asset
      seed: 1337,                   // hash isolation between assets
    },
    {
      name: 'shrub2',
      channel: 'b',
      texture: 'vegetation/shrub_02.png',
      sizeMin: 2.0,
      sizeMax: 6.0,
      yOffset: 0,
      threshold: 0.05,
      densityGamma: 1.0,
      densityScale: 0.5,
      maxInstances: 20000,
      seed: 3137,
    },
    {
      name: 'tree',
      channel: 'g',
      texture: 'vegetation/tree_01.png',
      sizeMin: 7.0,
      sizeMax: 14.0,
      yOffset: 0,
      threshold: 0.05,
      densityGamma: 1.0,
      densityScale: 1.0,
      maxInstances: 20000,
      seed: 3317,
    },
  ],
};

// ── Airfield ──────────────────────────────────────────────────────────────
// Static airfield model snapped to the terrain surface at runtime, with yOffset
// added on top for fine-tuning.
export const AIRFIELD = {
  position: { x: -8995.5, z: 2998.5 },  // world XZ (metres) — tile E5 centre
  yOffset: 0.2,                         // metres above local terrain surface
  rotationDeg: 0,                       // Y rotation (CCW from above; 90° = north-facing)
  scale: 1,                             // uniform scale multiplier

  // Asphalt detail shader on the 'runway' material — multiplies the existing
  // colour map by a tiled asphalt texture, reused as a bumpMap for fine-grain
  // self-shadowing. Live-updates from SimConfig each frame.
  runway: {
    blendStrength: 1,     // 0 = colour map unchanged, 1 = fully multiplied by asphalt
    uvScale: 32,          // asphalt repeats per UV unit
    bumpScale: 0.65,      // MeshStandardMaterial.bumpScale for asphalt detail
    // Distance fade on both the colour multiply and the bump — keeps the
    // high-frequency detail from aliasing at range and screaming through fog.
    fadeStart: 50,        // metres — detail is full strength up to here
    fadeEnd:   150,       // metres — detail is fully gone past here
  },

  // ── Runway 35 reference frame ────────────────────────────────────────────
  // Measured off the airfield GLB's `_runway` mesh. Everything the flight-school
  // traffic pattern is built from — hold-short point, line-up point, circuit
  // legs, threshold — is expressed as an offset in this frame rather than as
  // raw world XZ, so moving the airfield moves the whole pattern with it.
  //
  //   `along`   (t) metres down the runway in the departure direction (+ = toward
  //             the far end); t = 0 is the taxiway intersection, which is also
  //             where the Free Flight "Runway Takeoff" scenario spawns.
  //   `lateral` (s) metres right of the centreline looking down the runway
  //             (+ = east side, where the ramp is).
  //
  // See js/training/runwayFrame.js for the conversion helpers.
  runway35: {
    origin: { x: -9194.75, z: 3390 },  // world XZ of the centreline at t = 0
    heading: -10,                       // scenario heading pointing down the runway
    elevation: 73,                      // m MSL — field elevation
    halfWidth: 22.5,                    // m — centreline to paved edge
    approachEnd: -620,                  // t of the RWY 35 threshold (behind the origin)
    departureEnd: 1460,                 // t of the far end (ahead of the origin)
  },
};

// ── Lighting Scenarios ────────────────────────────────────────────────────

export const LIGHTING = {
  dusk: {
    name: 'Dawn',
    hdr: 'textures/qwantani_dusk_2_puresky_2k.hdr',
    background: 'textures/qwantani_dusk_2_puresky_2k_v3.jpg',
    environmentIntensity: 0.4,
    exposure: 0.55,
    effectBrightness: 0.65,
    instrumentBrightness: 0.5,
    fog: { color: 0x675e75, near: 0, far: 40000 },
    bloom: { enabled: true, strength: 0.06, radius: 0, threshold: 0 },
    filmGrain: { enabled: true, strength: 0.005 },
    contrast: { enabled: true, contrast: 1.65, brightness: 0 },
    sun: { enabled: true, color: 0xfebc90, intensity: 0.5, direction: [-0.6795, -0.2079, -0.7036] },
    ambient: { color: 0x8090b0, intensity: 0 },
    // See TERRAIN.grading for what each knob does.
    terrain: {
      brightness: 0.62,
      sunBoost: 8,
      ambientBoost: 1.47,
      tint: 0xd7dcfe,
      tintStrength: 1,
      saturation: 1.15,
    },
    // Scattered deck with warm, glowing sunlit rims against a cool dawn sky.
    clouds: {
      coverage: 0.47,
      density: 0.02,
      edgeSoftness: 0.18,
      albedo: 0xc7b7dc,
      sunScatter: 2.35,
      ambient: 0.25,
      altitude: { base: 1070, top: 2920 },
      noiseScale: 2500,
      wispStrength: 1.01,
      wispScale: 160,
      wispDrift: 3.5,
    },
  },
  afternoon: {
    name: 'Afternoon',
    hdr: 'textures/qwantani_afternoon_puresky_2k.hdr',
    background: 'textures/qwantani_afternoon_puresky_2k.jpg',
    environmentIntensity: 0.42,
    exposure: 0.8,
    effectBrightness: 1.0,
    instrumentBrightness: 0.0,    // emissive intensity of cockpit/instrument self-illumination
    fog: { color: 0xc4cbd3, near: 0, far: 40000 },
    bloom: { enabled: true, strength: 0.11, radius: 0.71, threshold: 1 },
    filmGrain: { enabled: false, strength: 0.005 },
    contrast: { enabled: true, contrast: 1.33, brightness: 0 },
    sun: { enabled: true, color: 0xfffefa, intensity: 2, direction: [0.4156, -0.7071, -0.5721] },
    ambient: { color: 0xfffaeb, intensity: 2 },
    terrain: { brightness: 1, sunBoost: 1, ambientBoost: 1, tint: 0xffffff, tintStrength: 0, saturation: 1 },
    // Bright fair-weather cumulus — moderate coverage, crisp sunlit tops.
    clouds: {
      coverage: 0.51,
      density: 0.02,
      edgeSoftness: 0.2,
      albedo: 0xa3b0b8,
      sunScatter: 0.96,
      ambient: 1.03,
      altitude: { base: 1230, top: 2420 },
      noiseScale: 1050,
      wispStrength: 2,
      wispScale: 80,
      wispDrift: 1.7,
    },
  },
  overcast: {
    name: 'Hazy',
    hdr: 'textures/kloofendal_misty_morning_puresky_2k_v2.hdr',
    environmentIntensity: 0.8,
    exposure: 0.5,
    effectBrightness: 1.0,
    instrumentBrightness: 0.0,    // emissive intensity of cockpit/instrument self-illumination
    fog: { color: 0xf5f5fa, near: 0, far: 40000 },
    bloom: { enabled: true, strength: 0.03, radius: 0, threshold: 0 },
    filmGrain: { enabled: true, strength: 0.005 },
    contrast: { enabled: true, contrast: 1.5, brightness: 0 },
    sun: { enabled: false, color: 0xd8dde3, intensity: 0, direction: [-0.3, -0.8, -0.3] },
    ambient: { color: 0xfef6e1, intensity: 0.64 },
    terrain: { brightness: 1, sunBoost: 1, ambientBoost: 1, tint: 0xffffff, tintStrength: 0, saturation: 1 },
    // Thick, flat overcast — high coverage, soft edges, no direct sun, sitting
    // low so the haze reads as a ceiling.
    clouds: {
      coverage: 0.87,
      density: 0.005,
      edgeSoftness: 1,
      albedo: 0xeef1f5,
      sunScatter: 0.96,
      ambient: 0.52,
      altitude: { base: 1820, top: 2070 },
      noiseScale: 1550,
    },
  },
  night: {
    name: 'Night',
    hdr: 'textures/NightSkyHDRI003_2K_HDR.hdr',
    // Starless plate — the HDRI's own stars are baked in at 2K (~6 arcmin per
    // pixel) and read as blurry smears up close, so they were medianed out of
    // NightSkyHDRI003_2K_HDR_2k.jpg (9 px window, q92 4:4:4) and
    // world/StarField.js draws real ones on top. A median rather than a blur
    // because stars are isolated few-pixel outliers that never win it, while
    // the moon (~60 px), its halo and the Milky Way nebulosity are wider than
    // the window and survive untouched. The .hdr above is untouched too: it
    // only feeds image-based lighting, where stars contribute no measurable
    // energy.
    background: 'textures/night_sky_starless_2k.jpg',
    // Real star positions from the Bright Star Catalogue. Presence of this block
    // is what enables the field; see SimConfig.STARS for the shared parameters.
    stars: { enabled: true, brightness: 1.0 },
    environmentIntensity: 0.4,
    exposure: 1.0,
    instrumentBrightness: 1.0,    // emissive intensity of cockpit/instrument self-illumination
    // Effects (smoke, dust, vegetation) bypass scene environmentIntensity, so
    // this pulls their environment-lit term down to match the dimmed night
    // scene. Emissive sparks/flares are unaffected.
    effectBrightness: 0.1,
    fog: { color: 0x05080f, near: 0, far: 30000 },
    bloom: { enabled: true, strength: 0.75, radius: 0.98, threshold: 0.62 },
    filmGrain: { enabled: true, strength: 0.005 },
    contrast: { enabled: true, contrast: 1.2, brightness: 0 },
    sun: { enabled: true, color: 0x80b7ff, intensity: 0.7, direction: [-0.9036, -0.3907, -0.1756] },
    ambient: { color: 0x2a3550, intensity: 0.35 },
    terrain: { brightness: 1, sunBoost: 0.44, ambientBoost: 1, tint: 0x0062ff, tintStrength: 0.17, saturation: 1 },
    // Sparse, dark clouds with faint cool moonlit rims.
    clouds: {
      coverage: 0.61,
      density: 0.005,
      edgeSoftness: 1,
      albedo: 0x9c9fa5,
      sunScatter: 0.91,
      sunStrength: 0.2,
      ambient: 0.38,
      altitude: { base: 1600, top: 2630 },
      noiseScale: 1950,
      wispStrength: 1.24,
      wispScale: 200,
      wispDrift: 5.3,
    },
  }
};

export const DEFAULT_LIGHTING = 'dusk';

// ── Environment ────────────────────────────────────────────────────────────
export const ENV = {
  airDensity: 1.225,          // kg/m³ (sea-level ISA) — reference ρ₀
  gravity: 9.81,              // m/s²
  referenceAirspeed: 80,      // m/s — dynamic-pressure reference for control/damping normalization
  // ISA troposphere: ρ(h) = ρ₀ · (1 − L·h/T₀)^((g/(R·L)) − 1), L = 0.0065 K/m,
  // T₀ = 288.15 K.
  isaLapseRate: 2.25577e-5,   // 1/m — L/T₀ for the (1 − k·h) base
  isaDensityExp: 4.2559,      // dimensionless exponent
  isaCeiling: 11000,          // m — tropopause; ρ flatlines above this
};

// ── Wind ──────────────────────────────────────────────────────────────────
export const WIND = {
  gustFrequency: 0.4,            // simplex noise sample rate (lower = smoother gusts)
  verticalScale: 0.3,            // vertical gust fraction
  defaultPreset: 'moderate',
  // Presets for the start-screen "Wind" selector. `base` is the prevailing wind
  // vector (m/s, +Z = headwind), `gust` the max deviation per axis; `dir`/`kts`
  // are display-only panel readouts.
  presets: {
    none:     { label: 'Calm',     base: [0.5, 0, -3], gust: 0.5,   dir: '170', kts: '02' },
    moderate: { label: 'Moderate', base: [0.5, 0, -3], gust: 1.0, dir: '170', kts: '06' },
    heavy:    { label: 'Heavy',    base: [-7, 0, 0],   gust: 2.5, dir: '270', kts: '14' },
  },
};

// ── Aircraft / Flight Model ────────────────────────────────────────────────
export const AIRCRAFT = {
  mass: 15000,
  wingArea: 47,
  wingSpan: 17.53,
  maxThrust: 80600,
  thrustLapse: 0.2,            // thrust loss fraction at thrustLapseSpeed
  thrustLapseSpeed: 250,       // reference speed for thrust lapse (m/s)
  engineRPM: {
    idle: 3000,                // RPM at zero throttle (idle)
    max: 16000,                // RPM at full throttle (TF34 max ~16,500)
    spoolTime: 5.0,            // seconds to reach ~63% of target RPM (time constant)
  },
  // Battle damage from enemy missile hits. Each strike kills the engine on the
  // struck side: thrust scales with the live engine count (1 → 0.5 → 0, applied
  // to the throttle so the audio winds down naturally), and a single live engine
  // yaws the nose toward the dead one (off-centre thrust).
  engineDamage: {
    thrustArm: 1.3,            // m — engine offset from centreline (asymmetric-thrust yaw arm)
    // Camera trauma decay rates. A strike/launch sets the corresponding trauma
    // to 1 and it decays linearly at these rates; amplitudes live in
    // CAMERA.cockpit.impactShake / .launchShake.
    impactShakeDecay: 3.0,     // 1/s — decays in ~0.33 s (quick and punchy)
    launchShakeDecay: 1.5,     // 1/s — decays in ~0.67 s (brief backblast wobble)
    // At/below this AGL a damaged airframe stops streaming the airborne
    // per-engine fire trails and emits a single buoyant ground heat plume
    // instead. Uses AGL, not the onGround contact flag, so a wreck resting in
    // any attitude still reads as grounded.
    groundFireAGL: 5,          // meters
    // Cadence of the ground heat plume, throttled so it can't flood the particle
    // pool (airborne per-engine trails still emit every frame).
    groundFireInterval: { min: 0.1, max: 0.2 }, // seconds
  },
  // Dust kicked up while the airframe scrapes the ground — gear-up belly slides
  // and wrecks grinding to a halt. Throttled like the heat plume, and gated on
  // speed so a stationary wreck stops pluming.
  groundDust: {
    minSpeed: 3,                          // m/s — below this, no dust
    interval: { min: 0.02, max: 0.05 },   // seconds between puffs
    scatter: 2.5,                         // m — XZ spawn jitter around the origin
  },
  dragCoefficients: {
    forward: { cd: 0.035, area: 47 },
    back:    { cd: 0.5,  area: 47 },
    left:    { cd: 0.8,  area: 28 },
    right:   { cd: 0.8,  area: 28 },
    up:      { cd: 0.5,  area: 30 },
    down:    { cd: 0.8,  area: 30 },
  },
  // Sideslip drag — the body-axis drag above barely resists a slip, since its
  // side term acts across the fuselage and only ~sin³β of it opposes motion.
  // This adds Cd = cd · sin²β (on wing area, along −velocity), saturating at
  // maxBetaDeg: slips past that are already the body-axis term's territory,
  // and full rudder near approach speed holds ~45°.
  sideslipDrag: { cd: 0.6, maxBetaDeg: 20 },
  liftParams: {
    clSlope: 5.7,
    clMax: 1.5,
    stallAngleDeg: 15,
    zeroLiftAngleDeg: -5,
  },
  // Roll: steady roll rate is roll authority ÷ roll damping per unit aileron —
  // 920k / 400k ≈ 2.3 rad/s (~130°/s) at full deflection, at any speed. The
  // Basic autopilot flies against AUTOPILOT.rollAuthority instead (see there).
  controlAuthority: { pitch: 600000, roll: 920000, yaw: 300000 },
  dampingCoefficients: { pitch: 400000, roll: 400000, yaw: 500000 },
  cgOffset: 0.08,
  // Weathervane stability — tail restoring torques from sideslip/AoA. Cn_beta
  // and Cm_alpha, pre-multiplied by (S · span).
  stabilityCoefficients: { yaw: 0.12, pitch: 0.06 },
  momentOfInertia: { pitch: 130000, roll: 50000, yaw: 170000 },
  // Post-stall behavior
  stallRecoveryMoment: 150000, // N·m per radian past stall — nose-down torque in stall
  stallDampingFactor: 0.3,     // minimum damping multiplier in deep stall (0–1)
  stallControlFactor: 0.4,     // minimum control authority multiplier in deep stall (0–1)
  airbrakeParams: {
    cdBoost: 0.08,             // added to forward Cd at full airbrake (split decelerons are big plates)
    minSpeed: 10.3,            // m/s (~20 kts) — airbrake disabled below this speed
  },
  gearParams: {
    cdBoost: 0.03,             // added to forward Cd with gear deployed (bulky struts + wheels)
  },
  flapParams: {
    clBoost: 0.6,              // added to CL at full flaps (linear with flap setting)
    cdBoost: 0.04,             // added to forward Cd at full flaps
    stallAngleBoostDeg: 5,     // extra stall angle at full flaps (degrees)
    pitchMoment: -80000,       // nose-down moment at full flaps (N·m), scales with q
  },

  // ── Total-destruction crash criteria ──────────────────────────────────────
  // Evaluated on the frame the airframe first contacts the ground
  // (Aircraft._evaluateCrash). Within ALL limits = a survivable gear or belly
  // landing; exceeding ANY one destroys the aircraft.
  crashDamage: {
    maxImpactSpeed: 10,                  // m/s — closing speed into the terrain normal
    maxBankAngle: 35 * Math.PI / 180,    // rad — past this at touchdown = wing strike
    minPitchAngle: -10 * Math.PI / 180,  // rad — nose-down past this = nose strike
    maxPitchAngle: 30 * Math.PI / 180,   // rad — nose-up past this = tail strike
    explosionSize: 2,                    // ParticleSystem.spawnExplosion size
  },
};

// ── Ground Contact ─────────────────────────────────────────────────────────
export const GROUND = {
  gearLength: 1.75,             // meters from gear pivot to wheel contact

  // Cosmetic Y-offset added to terrain height queries used for collision only
  // (wheels, belly, rest-height); positive = the aircraft sits higher above the
  // visual terrain. Weapon hit-tests, AI altitude and the gun pipper still query
  // the real surface.
  collisionYOffset: 0.18,

  // Contact spring/damper — tuned for stability at frame-rate timesteps
  springK: 450_000,            // N/m per wheel
  dampingC: 100_000,           // N·s/m per wheel
  // NOTE: uses 14000 kg, not AIRCRAFT.mass — kept for behavioral compatibility
  maxNormalPerWheel: 14000 * 9.81 * 2, // 2× reference aircraft weight

  muRoll: 0.025,               // rolling friction (rubber on concrete)
  muSide: 0.6,                 // lateral tire friction
  muBrake: 0.6,                // wheel braking friction
  brakeSmoothing: 6,           // per-second lerp speed for brake engagement

  angularDampCoeff: 200_000,   // N·m·s/rad — replaces aero damping at low airspeed

  // Transition speeds — shared between GroundContact and Controller autopilot
  vGround: 10,                 // m/s (~25 kts) — below this, pure ground mode
  vRotate: 50,                 // m/s (~107 kts) — above this, pure flight mode

  // Brake suspension pitch
  brakePitchMax: 2.0 * Math.PI / 180,  // rad — max nose-down pitch from braking
  brakePitchRate: 4,                   // smoothing rate (per second)

  // Nosewheel steering
  steeringAuthority: 120_000,  // N·m at full rudder deflection
  yawGroundDamping: 120_000,   // N·m·s/rad — prevents yaw oscillation on ground
  steeringMaxSpeed: 30,        // m/s — above this, deflection is at minimum
  steeringMinFraction: 0.15,   // minimum steering fraction at high speed (no full lockout)

  // Coulomb tyre scrub — kills the residual yaw that viscous damping only
  // asymptotes toward, so braking to a halt out of a turn stops the pivot.
  yawScrubTorque: 250_000,     // N·m at a dead stop (≈ muSide · weight · track arm)
  yawScrubSpeed: 2,            // m/s — faded out entirely above this

  // Belly (gear-up) landing
  bellyHeight: 1.0,            // meters — CG height when resting on the belly
  muBellyRoll: 0.4,            // belly sliding friction (metal on concrete)
  muBellySide: 0.8,            // belly lateral friction

  // Multiplies every ground friction coefficient once destroyed, so the wreck
  // ploughs in and grinds to a halt fast. Per-frame caps prevent overshoot.
  destroyedFrictionMult: 12,

  // Slack in the contact test for the cosmetic wheel animation only — contact
  // *forces* still use the strict (penetration > 0) test. Ground mode pins the
  // CG to restHeight, parking the deepest wheel at exactly zero penetration
  // (a strict test chatters), and one shared gearLength leaves each ray a few cm
  // off its own contact patch. Well under a wheel radius, so it never reads as
  // planted in flight.
  wheelContactTolerance: 0.15,
};

// ── Wheel animation (cosmetic) ─────────────────────────────────────────────
// Rolling wheels and nosewheel steering are purely visual — they read physics
// state and never feed back into it.
export const WHEELS = {
  fallbackRadius: 0.4,  // m — only used when a wheel mesh has no geometry to measure

  // Spin-up: a tyre meeting the runway is dragged up to the rolling rate by
  // contact-patch friction in a fraction of a second. First-order lag, per second.
  spinUpRate: 14,

  // Spin-down off the ground: drag proportional to rate (time constant, seconds)
  // plus a constant bearing friction (rad/s²) so it reaches a full stop instead
  // of asymptotically creeping.
  spinDownTau: 6,
  spinDownDecel: 1.5,

  // Nosewheel steering. The wheel is aimed along the track its own contact patch
  // cuts across the ground, so it can never appear to skate sideways;
  // maxSteerDeg is the mechanical stop it is clamped to.
  maxSteerDeg: 25,
  steerRate: 5,   // per-second lag toward the tracked angle (weight on nose)
  centerRate: 2,  // per-second lag back to centre once the nose wheel lifts

  // The track angle is an atan2 of contact-patch ground velocity, which
  // degenerates into noise near zero. Below trackMinSpeed the wheel holds its
  // angle, easing into full tracking by trackFullSpeed (m/s).
  trackMinSpeed: 0.4,
  trackFullSpeed: 2.5,

  // Suspension. The airframe tilts under flight loads and the oleo takes up the
  // difference, so a tyre is never left buried in the runway. Travel runs along
  // the leg's strut axis — the same axis GroundContact casts its contact ray
  // down — and only ever compresses: the GLB pose is full extension.
  maxTravel: 0.35,   // meters of stroke before the oleo bottoms out
  reboundRate: 7,    // per-second lag on extension; compression is instant

  // Touchdown dust, triggered by contact-patch slip rather than contact alone:
  // the tyre arrives stopped and is scrubbed up to speed, and that scrub throws
  // the dust. A wheel skipping a bump mid-rollout already turns at ground speed,
  // so it stays clean.
  touchdownSlip: 4,   // m/s of scrub before a touchdown kicks up a puff
  touchdownDrag: 0.3, // fraction of aircraft velocity the puff trails at
};

// ── Autopilot / Controller ─────────────────────────────────────────────────
export const AUTOPILOT = {
  sensitivity: 0.002,          // rad/pixel
  throttleStep: 0.05,          // per scroll tick

  // Bank-to-turn gains — PD on bank/pitch error, D term on body-frame angular
  // velocity (rad/s). The War Thunder feel comes from the tan(off-bore) input
  // curve in Controller.js, not from tuning these.
  rollGain: 3.5,               // P-gain: bank error (rad) → ailerons
  rollDamping: 0.3,            // D-gain: body roll rate (rad/s) → ailerons
  pitchGain: 1.4,              // P-gain: pitch error → elevator
  pitchDamping: 0.3,           // D-gain: body pitch rate − coordinated-turn feed-forward (rad/s) → elevator

  // Gunnery-zoom gains — RMB in cockpit switches the autopilot from bank-to-turn
  // into "point-the-bore" mode: bankTarget lerps to 0 and the rudder yaws the
  // nose onto the look-vector, so the crosshair acts as an FPS-style cursor.
  zoomRollGain: 6.5,           // higher P on (0 - currentBank) → wings drive level
  zoomRollDamping: 0.5,
  zoomPitchGain: 2.6,
  zoomPitchDamping: 0.9,
  zoomYawGain: 4.0,            // P-gain: body yaw error → rudder
  zoomYawDamping: 0.6,         // D-gain: body yaw rate (rad/s) → rudder
  rudderGain: 0.3,
  rudderGainGround: 2.0,       // stronger rudder during ground roll / transition

  // Look-to-bank sharpening: tan(min(offBore·lookSharpness, maxBank)). 1.0 =
  // linear (5° look → 5° bank), higher = punchier turns for smaller offsets. At
  // 2.0 a 30° look offset already commands the 60° low-speed cap.
  lookSharpness: 2.0,

  // Speed-adaptive bank cap for an on-horizon target; smaller look offsets
  // command proportionally less bank via lookSharpness. Below-horizon targets
  // naturally tilt past this (bank > 90°, inverted). Set so 1/cos(bank) stays
  // within sustainable G.
  maxBankAngleLow:  60 * Math.PI / 180,   // 60° at low speed (2G horizon turn)
  maxBankAngleHigh: 75 * Math.PI / 180,   // 75° at high speed (~4G horizon turn)
  bankSpeedLow: 40,            // m/s — below this, use low bank limit
  bankSpeedHigh: 80,           // m/s — above this, use high bank limit

  // Protection and thresholds
  aoaProtectionStart: 0.6,     // start reducing pull at 60% of stall angle
  negativeGLimit: -1.0,        // floor on elevator command (default -1 = saturation only)
  wingsLevelThreshold: 5 * Math.PI / 180, // heading error below which wings-level bias kicks in

  // Nosewheel steering (PD controller)
  groundSteeringGain: 3.0,     // heading error → rudder
  groundSteeringDamp: 3.0,     // yaw-rate damping (D-term)

  // Roll authority (N·m at full aileron) the look-to-steer autopilot is tuned
  // against. The airframe's own (AIRCRAFT.controlAuthority.roll) was raised for
  // the manual schemes; Basic scales its ailerons' effect back to this, so its
  // roll feel — ~57°/s at full aileron, WASD takeover included — is unchanged.
  rollAuthority: 400000,
};

// ── Control schemes ────────────────────────────────────────────────────────
// How the player's hands reach the flight controls. `basic` is the look-to-
// steer autopilot above (WASD/QE take an axis over while held); every other
// scheme flies through FlightAssist, a stability layer between the stick and
// the surfaces that is always on. See input/ControlScheme.js for the scheme
// table and input/bindings.js for the default bindings.
export const CONTROLS = {
  // Keyboard flight axes ramp toward their key instead of snapping, so a tap
  // is a nudge and a hold is a full deflection a third of a second later.
  keyboard: {
    rampUp: 3.0,               // 1/s — toward a held key's full deflection
    rampDown: 6.0,             // 1/s — back to centre once released (and through it on a reversal)
    throttleRate: 0.4,         // 1/s — Shift/Ctrl throttle (keyboard-lock fullscreen only)
  },

  // Basic scheme: a held WASD/QE key takes that axis away from the look-to-
  // steer autopilot. It hands back once the key has been released for
  // `holdAfter`, blending over `blendOut`, so a quick tap-tap of A doesn't
  // flicker the autopilot in and out between the taps.
  takeover: {
    blendIn: 0.12,             // s — autopilot → keyboard
    holdAfter: 0.35,           // s — key released, keyboard still owns the axis
    blendOut: 0.6,             // s — keyboard → autopilot
    rollRateMax: 0.9,          // rad/s at a full roll key — Basic's pre-existing ~52°/s, untapered
  },

  // Manual (mouse + keyboard): the mouse moves a virtual stick that stays
  // where it's left, drawn on the HUD as a dot in a ring.
  mouseStick: {
    gain: 1 / 350,             // stick travel per pixel at 1.0× mouse sensitivity (≈350 px = full)
    deadzone: 0.03,            // radial, of full travel
  },

  // Body-frame head look for every scheme but Basic (whose view is the aim).
  head: {
    yawLimit: 160 * Math.PI / 180,
    pitchUpLimit: 85 * Math.PI / 180,
    pitchDownLimit: -50 * Math.PI / 180,
    returnRate: 6,             // 1/s — Manual mouse: view eases forward when Space is released
    // Analogue look (gamepad right stick, joystick hat) is spring-centred: the
    // deflection *is* the head angle, so letting go looks forward again.
    stickYaw: 150 * Math.PI / 180,
    stickPitchUp: 70 * Math.PI / 180,
    stickPitchDown: -40 * Math.PI / 180,
  },

  // Analogue devices (Gamepad API). Deadzone is the player's setting
  // (SETTINGS.stickDeadzone); expo softens the centre for fine tracking.
  gamepad: {
    expo: 0.35,                // 0 = linear, 1 = cubic
    rudderRamp: 4.0,           // 1/s — LB/RB are digital, so they ramp like keys
    throttleRate: 0.5,         // 1/s — D-pad up/down held
    slewRate: 700,             // seeker slew, mouse-pixel equivalents per second at full stick
    holdTime: 0.6,             // s — press length that turns a tap button into its hold action
  },
  joystick: {
    expo: 0.25,
    rudderDeadzone: 0.12,      // twist grips rarely centre cleanly — extra deadzone on yaw
    throttleTakeover: 0.02,    // slider travel that has to move before it overrides wheel/key throttle
    slewRate: 80,              // hat slew on press, mouse-pixel equivalents per second
    slewRateMax: 250,          // hat slew after holding for slewRampTime
    slewRampTime: 1.5,         // s — hold time to ramp from slewRate to slewRateMax
    hatLookRate: 4,            // 1/s — hat look snaps ease toward their angle
  },

  // Flight assist — always on outside Basic. The stick asks for a load factor
  // rather than a surface deflection: centred holds the current flight path
  // (with bank compensation, so a turn holds its altitude), full aft asks for
  // `nMax`, and an AoA limiter fades the pull off approaching the stall. Pitch
  // is a PI loop on body pitch rate, whose integrator is the auto-trim. Roll
  // asks for a rate and holds whatever bank the stick was centred at — no
  // snap to wings-level, so a shallow bank stays put.
  assist: {
    nMax: 7.0,                 // G the stick works toward (A-10 limit 7.33)
    nMin: -2.5,                // G at full forward stick
    alphaLimitFrac: 0.85,      // fraction of the (flap-boosted) stall AoA the limiter holds
    // Soft stops, not hard ones: the last stretch of stick travel past
    // `overrideStart` overrides both limits, so a deliberate full pull
    // over-G's (toward `nOverload`) and, slow, carries the wing through the
    // stall horn (VWS.stallAoAOn) into a real stall (`alphaOverrideFrac`).
    // Protection is for normal flying, not a wall.
    overrideStart: 0.8,        // |stick| where the override begins (smoothstep to full)
    nOverload: 9.0,            // G at full aft stick
    alphaOverrideFrac: 1.25,   // AoA limit at full stick, as a fraction of the stall angle
    alphaGain: 4.0,            // 1/s — pitch rate removed per radian of AoA past the limit
    pitchKp: 5.0,              // elevator per rad/s of pitch-rate error
    pitchKi: 12.0,             // elevator per rad of accumulated pitch-rate error
    bankCompFloor: 0.05,       // softens the 1/cos(bank) hold toward knife-edge (see FlightAssist)
    // Full stick asks for this fraction of the airframe's steady roll rate
    // (roll authority ÷ roll damping, read live, so the assist follows the
    // airframe). Below `rollTaperSpeed` the demand tapers with airspeed — the
    // real roll rate falls off slow, and keeps finals from being twitchy —
    // down to `rollTaperFloor` of it.
    rollRateFrac: 0.95,
    rollTaperSpeed: 80,        // m/s
    rollTaperFloor: 0.35,
    rollKp: 1.5,               // aileron per rad/s of roll-rate error
    bankHoldGain: 2.0,         // 1/s — bank error → roll-rate demand while the stick is centred
    airborneBlend: 1.0,        // s — direct stick → assisted pitch after the wheels leave
    // On the ground the roll axis also steers the nosewheel (fading out by
    // rotation speed), so A/D or a stick taxis without reaching for the rudder.
    groundRollSteer: 1.0,
  },
};

// ── Camera ─────────────────────────────────────────────────────────────────
export const CAMERA = {
  chase: {
    fov: 50,                    // degrees
    zoomFov: 28,                // degrees — right-click zoom FOV
    // Multiplier on the follow distance while zoomed. A narrower FOV magnifies
    // the airframe as much as the world behind it, which would just fill the
    // frame with A-10; pulling the camera back by roughly the FOV ratio
    // (tan25°/tan14° ≈ 1.9) keeps it about the same on-screen size and spends
    // the magnification on the background, like a telephoto lens.
    zoomDistanceScale: 1.0,
    offset: [0, 0, 26],        // behind aircraft (x, y, z)
    destroyedDistance: 100,    // m — pull-back distance once the A-10 is destroyed (CameraMode.DESTROYED)
    lookSmoothing: 6.0,
    positionSmoothing: 12,
    positionSmoothingY: 6,
    lookaheadScale: 0.035,        // meters of lookahead per m/s of airspeed
    lookaheadMax: 4,           // cap so it doesn't fly off at high speed
    // Manual schemes: the camera follows the airframe's whole attitude (so it
    // stays behind you over the top of a loop) rather than a level look
    // direction. It chases that attitude at this rate, which is the roll lag.
    bodyFollowSmoothing: 5,
    bodyPitch: -0.06,          // rad — slight look-down onto the airframe
    bodyLift: 3,               // m — camera rides this far above the airframe, along its up axis
  },
  cockpit: {
    fov: 70,                    // degrees — wider for cockpit immersion
    zoomFov: 40,                // degrees — right-click zoom FOV
    maverickAimFov: 50,         // degrees — narrower view while in "Aim Maverick" mode (focuses on the TV)
    lookSmoothing: 12.0,
    rollSmoothing: 4.0,
    rollFraction: 0.25,          // how much of aircraft roll to apply (0–1) — Basic (look-to-steer) only
    // Manual schemes lock the view to the airframe instead (full roll, loops);
    // switching schemes mid-flight eases between the two frames at this rate.
    frameBlendRate: 4,
    // Radians — purely visual tilt below the look-vector, so more of the
    // instrument panel is visible. The autopilot still tracks the raw mouse
    // look direction.
    pitchOffset: -0.15,
    // Meters from the pilot's eye point at which the cockpit LOD swaps (HD
    // interior ⇄ SD exterior shell + pilot) during a camera transition in or
    // out of the cockpit. Sized to the canopy, so the swap happens while the
    // camera is passing through the airframe and the frame is filled with
    // structure, rather than in open air where it reads as a pop.
    lodSwapRadius: 2.5,
    headLean: -0.15,           // meters — max lateral head lean at full yaw
    gForceSmoothing: 8.0,
    fallbackOffset: [0, 2.5, 3], // above and forward of aircraft origin
    // Head spring-damper — simulates pilot head inertia
    headSpring: {
      // Damping ratio ζ = damping / (2·√stiffness). Both springs sit near 0.6 —
      // one soft overshoot, then settled. Much below that and a hard roll-out
      // (pitch rate collapsing at once) sets the head bobbing for seconds.
      stiffness: 25,             // spring constant (higher = snappier return)
      damping: 6,                // damping coefficient (higher = less oscillation)
      maxDisplacement: 0.02,     // meters — max head offset from rest position
      maxRotation: 0.5,          // radians — max rotational displacement from inertia
      rotStiffness: 16,          // rotational spring stiffness (pitch/yaw)
      rotDamping: 5,             // rotational damping (pitch/yaw)
      // Roll is stiffer and better damped, so bank-induced sway stays small and
      // settles fast instead of lolling around.
      rollStiffness: 40,
      rollDamping: 8,
    },
    // "Sense of flight" — manual schemes only, where the view is otherwise
    // bolted to the airframe's roll. The head is a damped follower that lags a
    // roll and settles a beat after it stops, tilts a little back toward the
    // horizon in a held bank (the pilot's head-levelling reflex), and tips away
    // from lateral G in a skid. Each term makes a target tilt relative to the
    // airframe, and one spring chases the sum, so the terms never step. Keep it
    // small — a few degrees reads as a head, and a dozen reads as a broken camera.
    senseOfFlight: {
      rollLag: 0.035,            // s — tilt per rad/s of roll rate (≈4° at 125°/s)
      rollLagMax: 0.09,          // rad — cap on the lag term
      horizonLevel: 0.07,        // rad — tilt toward level at 90° bank (sin-shaped, 0 inverted)
      lateralG: 0.08,            // rad per G of side force
      lateralGMax: 0.06,         // rad — cap on the side-force term
      verticalFade: [0.75, 0.95],// |nose-up/down| range over which the horizon term fades (bank is undefined vertical)
      stiffness: 30,             // follower spring
      damping: 8,                // ζ ≈ 0.73
    },
    runwayShake: {
      amplitude: 0.002,          // radians — max rotational shake at full intensity
      posAmplitude: 0.002,        // meters — max positional shake at full intensity
      frequency: 6,             // noise sample rate (higher = faster vibration)
      speedMin: 0,               // m/s — shake starts ramping in
      speedMax: 70,              // m/s — shake reaches full intensity
    },
    gunShake: {
      amplitude: 0.002,          // radians — max rotational shake while firing
      posAmplitude: 0.001,       // meters — max positional shake while firing
      frequency: 30,             // noise sample rate (high = rapid vibration from GAU-8)
    },
    // High-AoA airframe buffet — ramps in with AoA as the wing approaches stall.
    // Positional shake only, in flight only (runwayShake covers the ground).
    // aoaStart/aoaFull/minSpeed also drive the cockpit buffet rumble (via
    // Aircraft.buffetIntensity), so the sound always starts with the shake.
    buffetShake: {
      posAmplitude: 0.0025,      // meters — max positional shake at full buffet
      frequency: 12,             // noise sample rate (higher = faster vibration)
      aoaStart: 0.3,             // fraction of stallAngle where buffet begins
      aoaFull: 1.1,              // fraction of stallAngle for full intensity
      minSpeed: 20,              // m/s — below this the buffet is suppressed (AoA is noisy)
      damageIntensity: 0.4,      // 0–1 buffet held while any engine is knocked out
    },
    // Missile-impact jolt on an enemy strike, scaled by aircraft.impactTrauma²
    // (decays per AIRCRAFT.engineDamage.impactShakeDecay). Shakes view + head.
    impactShake: {
      amplitude: 0.025,           // radians — peak rotational shake at full trauma
      posAmplitude: 0.04,         // meters — peak positional shake at full trauma
      frequency: 28,              // noise sample rate (fast, jarring rattle)
    },
    // Backblast wobble as a rocket leaves the rail, scaled by launchTrauma²
    // (decays per AIRCRAFT.engineDamage.launchShakeDecay). Far gentler than the
    // impact jolt.
    launchShake: {
      amplitude: 0.003,           // radians — peak rotational shake at launch
      posAmplitude: 0.004,        // meters — peak positional shake at launch
      frequency: 26,              // noise sample rate (fast rocket-motor rattle)
    },
    // Head bobble as the pilot snaps the goggles down or up. Self-contained in
    // CockpitCamera: the toggle sets its own trauma to 1, which decays here and
    // scales the shake by trauma². Mostly positional, like a nod.
    nvgShake: {
      amplitude: 0.012,           // radians — peak rotational sway at the flip
      posAmplitude: 0.01,         // meters — peak positional sway at the flip
      frequency: 0.5,               // noise sample rate (slow, smooth organic drift)
      decay: 1.7,                 // 1/s — trauma rings out over ~0.6 s
    },
  },
  enemy: {
    fov: 40,                     // degrees
    distance: 35,                // meters — orbit radius around the enemy
    minDistance: 12,             // closest scroll-wheel zoom
    maxDistance: 200,            // furthest scroll-wheel zoom
    zoomFactor: 1.12,            // multiplicative radius step per wheel notch
    lookSmoothing: 18,           // exp-decay rate for mouse orbit input
    zoomSmoothing: 10,           // exp-decay rate easing the orbit radius
    groundClearance: 2,          // meters — the orbit never sinks below terrain + this
  },
  // Start-screen orbit camera (CameraMode.START) — OrbitControls-style drag +
  // scroll-zoom around the parked A-10, with a gentle idle auto-rotate.
  start: {
    fov: 38,                     // degrees
    distance: 24,                // meters — initial orbit radius
    minDistance: 18,             // closest zoom
    maxDistance: 36,             // furthest zoom
    pitch: 4 * DEG2RAD,          // initial look-down angle
    yawOffset: -150 * DEG2RAD,   // initial orbit yaw relative to spawn heading (3/4 rear view)
    minPitch: -70 * DEG2RAD,
    maxPitch: 5 * DEG2RAD,       // keep the camera above the ramp
    targetHeight: 1,             // meters — focal point above aircraft origin
    // Fraction of distance the look-at shifts left, so the A-10 sits
    // right-of-center and clears the left UI panel (0 = centered).
    targetLateralOffset: 0.1,
    lookSmoothing: 8,            // exp-decay rate easing orbit angles + zoom
    dragSensitivity: 0.005,      // radians per pixel of drag
    zoomFactor: 0.9,             // multiplier per wheel notch (zoom in)
    autoRotateSpeed: -0.02,      // rad/s idle drift
    idleDelay: 3.0,              // seconds of no input before auto-rotate resumes
    moveInfluence: -0.001,       // orbit spin velocity (rad/s) added per pixel of mouse motion
    moveDamping: 4.0,            // exp-decay rate of that spin velocity
    maxSpinSpeed: 4.0,           // rad/s cap on mouse-motion spin velocity
    introDuration: 0.65,         // seconds — cinematic glide from the beauty shot into the cockpit
  },
  // Fixed "action camera" mounts baked into the model (CameraMode.ACTION, C key).
  // One shot per node whose name starts with `prefix`, cycled in name order; the
  // rest of the shot is read off that name, so adding a mount is a Blender
  // export rather than a code change. See camera/ActionCamera.js.
  action: {
    prefix: 'cam_',              // node-name prefix marking a mount (lower-cased compare)
    tagCockpit: '_cockpit',      // name contains this → draw the HD interior, not the SD shell
    tagPilot: '_pilot',          // name contains this → draw the seated pilot (interior shots only;
                                 // an untagged exterior mount is dressed like the chase view)
    // How an authored lens is mapped onto the live window. 'horizontal' holds
    // the focal length's horizontal field of view and spends a taller window on
    // extra height; 'vertical' uses the exported vertical angle as-is.
    fovFit: 'horizontal',
    fallbackFov: 40,             // degrees — used when a mount exported as a plain empty
    transitionDuration: 0.5,     // seconds — glide between mounts (and in/out of the mode)
  },
  fovSmoothing: 6.0,           // shared FOV transition rate (exp decay)
};

// ── Audio ─────────────────────────────────────────────────────────────────
export const AUDIO = {

  startup:  { duration: 44 },    // seconds — length of Startup*.wav files
  shutdown: { duration: 35 },    // seconds — length of Shutdown*.wav files
  transitionFade: 10,             // seconds — crossfade between one-shots and loops

  crossfadeDuration: 0.33,       // seconds — cockpit ↔ external blend (match camera transition)

  // Global master-volume fades at start-screen ↔ scenario boundaries, so audio
  // never pops in at full volume. Each matches the visual transition it rides.
  enterFade: 0.65,               // seconds — matches CAMERA.start.introDuration
  exitFade: 1.2,                 // seconds — matches the fade-to-black back to the start screen

  // Start-screen music — an independent HTMLAudioElement (separate from the
  // per-scenario AudioManager voices) looping whenever the start screen is
  // shown, faded out and stopped when the player enters a scenario.
  music: {
    file: 'assets/sound/la-defaite-gerald-olivieri.mp3',
    volume: 0.4,                 // base volume, further scaled by the user music + master volumes
    fadeOut: 8,                  // seconds — fade out then stop, on entering a scenario
    victoryStart: 0,             // seconds — offset the theme starts at on mission complete
    victoryFadeIn: 1,            // seconds — fade the victory theme in
    victoryWorldFadeOut: 1.5,    // seconds — duck world/A-10 audio as the theme swells
  },

  // gainMul/pitchMul post-multiply the formula-driven values from the
  // sound-library documentation.
  sounds: {
    // ── External ────────────────────────────────────────
    Idle:               { gainMul: 0.4, pitchMul: 1 },
    Idle_Front:         { gainMul: 0.4, pitchMul: 1 },
    Idle_Back:          { gainMul: 0.4, pitchMul: 1 },
    Maximum_Turbine:    { gainMul: 0.4, pitchMul: 1 },
    Maximum_Fan:        { gainMul: 0.4, pitchMul: 1 },
    Maximum:            { gainMul: 0.4, pitchMul: 1 },
    Maximum_Front:      { gainMul: 0.4, pitchMul: 1 },
    Distant:            { gainMul: 0.4, pitchMul: 1 },
    Distant_Front:      { gainMul: 0.4, pitchMul: 1 },
    Distant_Back:       { gainMul: 0.4, pitchMul: 1 },
    Flyby:              { gainMul: 0.4, pitchMul: 1 },
    // ── Cockpit ─────────────────────────────────────────
    Idle_cockpit:       { gainMul: 0.4, pitchMul: 1 },
    Maximum_cockpit:    { gainMul: 0.4, pitchMul: 1 },
    Avionics_cockpit:   { gainMul: 0.4, pitchMul: 1 },
    Wind_cockpit:       { gainMul: 0.4, pitchMul: 1 },
    Turbulence_cockpit: { gainMul: 0.4, pitchMul: 1 },
    Ground_cockpit:     { gainMul: 1, pitchMul: 1 },
  },

  // Ground cockpit rumble — maps ground speed to gain/pitch
  groundRumble: {
    speedMax: 70,        // m/s — gain reaches 1 at this speed
  },

  // Airborne buffet rumble — the same Ground_cockpit loop, faded in with
  // Aircraft.buffetIntensity (onset/full AoA set by CAMERA.cockpit.buffetShake)
  buffetRumble: {
    gain: 1,             // gain at full buffet (before gainMul)
  },

  // Turbulence cockpit — maps pitch rate to gain
  turbulence: {
    maxPitchRate: 1.0,   // rad/s at which turbulence gain reaches 1
  },

  // Distance attenuation radii (meters) — for future spatial audio
  distance: {
    distantMin: 100,  distantMax: 5000,
    otherMin: 100,    otherMax: 1500,
  },

  // Engine-loop distance attenuation, external views only. The loops are
  // non-positional THREE.Audio, so without this they play at full volume even
  // from the enemy-orbit camera, which can sit kilometres from the A-10.
  // Chase and start-orbit stay inside refDistance; farther views roll off
  // with inverse-distance and taper to silence at maxDistance. Cockpit loops are
  // never attenuated (you're sitting in the jet).
  engineDistance: {
    refDistance: 80,     // m — full volume within this radius
    maxDistance: 3000,   // m — beyond this the external engine loops are silent
  },

  // Distant-aircraft proximity loop: one shared voice on the Distant.wav buffer
  // whose gain swells with the CUMULATIVE proximity of every jet in range, so a
  // jet wooshes louder as it nears and two are louder than one. Enemy planes
  // always contribute; the A-10 only from the detached enemy-orbit camera,
  // where its near-field loops have already faded out. Cockpit view ducks the
  // whole mix (canopy muffling).
  distantJets: {
    maxDistance: 7000,   // m — beyond this a jet contributes nothing
    fullDistance: 250,   // m — at/under this a single jet contributes its full 1.0
    volume: 0.6,         // master gain at full proximity
    maxGain: 1.3,        // clamp on the summed proximity (a couple of jets may exceed one)
    speedRef: 70,        // m/s — slower jets contribute proportionally less
    cockpitMul: 0.4,     // volume multiplier when fully in cockpit view
    fadeTime: 0.4,       // s — smoothing on the summed gain (kills camera-switch pops)
  },

  // Per-enemy-type proximity loops — same cumulative, non-positional model as
  // `distantJets` above (one shared voice per type, gain driven by the summed
  // proximity of every live enemy carrying that `audioLoop` tag), with two
  // differences:
  //
  //   • Range is realistic rather than generous. Jets are deliberately audible
  //     from kilometres out because they're a gameplay cue; a helicopter or a
  //     BTR column should only be heard on a genuinely low, close pass and must
  //     be inaudible from a few hundred feet overhead.
  //   • `falloff` bends the near-term curve. distantJets ramps linearly from
  //     maxDistance to fullDistance; an exponent > 1 here keeps the last stretch
  //     of that ramp near zero, so the loop stays quiet until you're close and
  //     a group of contributors can't sum its way into audibility from far off.
  //
  // `speedRef` (optional) scales a contributor by its own ground speed, so a
  // halted or knocked-out vehicle stops feeding the *driving* loop. Destroyed
  // wrecks never contribute at all.
  enemyLoops: {
    helicopter: {
      sound: 'Helicopter_Loop',
      fullDistance: 60,    // m — at/under this a single helicopter contributes 1.0
      maxDistance: 300,    // m — beyond this it contributes nothing
      falloff: 3.5,        // curve exponent on the ramp between the two
      volume: 0.7,         // master gain at full proximity
      maxGain: 1.4,        // clamp on the summed proximity
      cockpitMul: 0.35,    // volume multiplier when fully in cockpit view
      fadeTime: 0.35,      // s — smoothing on the summed gain
    },
    groundVehicle: {
      sound: 'Vehicle_Driving_Loop',
      fullDistance: 40,
      maxDistance: 200,
      falloff: 4,
      volume: 0.75,
      maxGain: 1.5,
      cockpitMul: 0.3,
      fadeTime: 0.35,
      speedRef: 3,         // m/s — below this the vehicle isn't really driving
    },
    tunguska: {
      sound: 'Vehicle_Idle_Loop',
      fullDistance: 30,
      maxDistance: 150,
      falloff: 3.5,
      volume: 0.6,
      maxGain: 1.3,
      cockpitMul: 0.3,
      fadeTime: 0.35,
    },
  },

  // Explosion one-shots — fired with propagation delay + distance falloff,
  // baked at trigger time relative to the camera's world position.
  explosions: {
    speedOfSound: 343,    // m/s — air at ~20°C, sea level
    refDistance: 1000,    // m — distance at which gain = sound.volume
    maxDistance: 8000,    // m — events farther than this are dropped
    minVolume: 0.001,     // skip scheduling if attenuated volume falls below this
    sounds: {
      Explosion_AIM9:          { file: 'Explosion_AIM-9.wav',           volume: 1 },
      // Muffled interior boom, played instead of the external blast when the
      // A-10 is struck and the player is in cockpit view.
      Explosion_AIM9_Interior: { file: 'Explosion_AIM-9_Interior.wav',  volume: 1 },
      Explosion_Crash:         { file: 'Explosion_Crash.wav',           volume: 1 },
    },
  },
};

// ── Radar Warning Receiver ────────────────────────────────────────────────
// Cockpit avionics warning about radar/missile-equipped enemies, driven each
// frame from the live enemy list: `radarEquipped` entities feed the sweep/
// contact logic, `missileEquipped` ones raise the steady tracking tone inside
// `trackingRange`. Audible in chase + cockpit views only.
export const RWR = {
  // The tracking tone fires whenever a missile-equipped enemy is this close.
  // ~3× ENEMY_MISSILE.maxRange, so the warning arrives well before the player
  // can actually be engaged.
  trackingRange: 9260,            // m — 5 NM
  // Sweep cadence: every blipInterval the receiver fires one blip per enemy in
  // contact, staggered by blipStagger so a crowded sky reads as a fast sweep
  // rather than a chord. Contact is the entity's own `radarContact` flag where
  // it models detection (e.g. the Tunguska's cone + terrain masking), otherwise
  // a plain max-range sphere from GAME_HUD.target.maxDistance.
  blipInterval: 3.0,              // s — between successive sweeps
  blipStagger: 0.12,              // s — gap between blips inside one sweep
  trackingFadeTime: 0.5,          // s — exponential fade, avoids clicks as threats wink in/out
  // Per-sound mix levels (post-multipliers on master gain).
  contactVolume: 0.5,
  launchVolume: 0.7,
  blipVolume: 0.45,
  trackingVolume: 0.3,
};

// ── Voice Warning System (GPWS-style spoken cautions) ───────────────────────
// Driven purely off cached AGL (aircraft.altitudeAGL) + vertical speed — no
// extra terrain query. Audible only in cockpit/chase views, like the RWR, and
// suppressed with the gear down (normal low passes / landings).
export const VWS = {
  // "Altitude, altitude" — fires once on descending through altitudeFloor, and
  // re-arms only above floor + hysteresis, so cruising near the floor doesn't
  // machine-gun it.
  altitudeFloor: 150,        // m AGL — ~500 ft awareness floor
  altitudeHysteresis: 40,    // m — must climb this far above the floor to re-arm
  altitudeVolume: 0.5,

  // "Pull up" — fires below pullupCeiling when AGL / sink rate drops under
  // pullupTimeToImpact, repeating rather than spamming per-frame.
  pullupCeiling: 120,        // m AGL — only evaluated this low (cheap gate)
  pullupTimeToImpact: 4.0,   // s — predicted seconds to ground at current sink rate
  pullupMinSinkRate: 2.0,    // m/s — ignore shallow descents (avoids false alarms)
  pullupRepeat: 1.65,        // s — gap between repeated callouts while in danger
  pullupVolume: 0.5,

  // Gear is "down enough" to suppress warnings above this fraction (0=up,1=down).
  gearDownThreshold: 0.2,

  // "Master caution" — continuous loop while an engine is knocked out. Bound
  // like the stall buffet loop (cockpit + chase views).
  masterCautionVolume: 0.7,
  masterCautionFadeTime: 0.1,   // s — exponential fade on the loop envelope

  // ── Stall buffet warning ──────────────────────────────────────────────────
  // Continuous loop as the wing approaches its stall angle, starting slightly
  // early (stallAoAOn < 1.0) with hysteresis so it can't chatter at the
  // boundary. Suppressed on the ground and below stallMinSpeed, where alpha
  // (derived from velocity direction) goes noisy and a real stall is impossible.
  stallAoAOn: 0.95,          // fraction of stallAngle at which the warning starts
  stallAoAOff: 0.85,         // |alpha| must drop below this fraction to stop
  stallMinSpeed: 20,         // m/s — below this the warning is suppressed
  stallFadeTime: 0.05,       // s — exponential fade on the loop envelope
  stallVolume: 0.75,
};

// ── Mission flow ──────────────────────────────────────────────────────────
// Pacing of the end-of-mission beat, kept out of the scoring config in
// MissionStats because it is presentation, not points.
export const MISSION = {
  // s of ordinary gameplay after the last enemy dies, before "MISSION COMPLETE"
  // takes over. The final kill's explosion, debris and audio get to land on
  // their own instead of being stepped on by the victory theme and the ducking
  // of all world audio.
  completeDelay: 6,
};


// ── Mission radio ──────────────────────────────────────────────────────────
// Everything the player hears over the radio, driven entirely by observed world
// state (see mission/JTACRadio.js) — no scripting per scenario. Two nets share
// one voice, because one voice is what a headset has:
//
//   • **Tower** (`atc`, clips under public/assets/sound/atc/) — taxi and takeoff
//     clearance and the handoff to the controller. Plays in Free Flight as well
//     as Combat, but never in Flight School, where the instructor owns the net.
//   • **JTAC** (everything else, clips under public/assets/sound/jtac/) — the
//     ground controller, Combat only, and deliberately held off the air until
//     the tower has finished handing the flight over (`atc.jtacDelay`).
//
// A cue's `net` says which it belongs to; omitted means `jtac`.
//
// `cues` is the whole vocabulary: each entry names the clips that can serve it
// (filename stems under public/assets/sound/jtac/) and the etiquette that
// governs it. The director requests a cue; the radio decides whether, when and
// with which recording to transmit it.
//
//   priority  — ranks a cue against the others waiting, and against whatever is
//               already transmitting (see `preemptMargin`).
//   cooldown  — s before the same cue may be requested again.
//   maxPlays  — hard cap per mission, for lines that are a nice beat once or
//               twice and tedious the fifth time (the ingress script).
//   ttl       — s a queued cue stays relevant; past that the moment has gone
//               and it is binned rather than transmitted late. Defaults to
//               `queueTTL`; a follow-up line that has to wait out the call it
//               answers needs its own, longer one.
//   clips     — the recordings that can serve it. A cue may carry extra named
//               pools beside `clips` (e.g. `gunClips`) for a variant of the
//               same call: the requester names the pool, so one cue — one
//               priority, one cooldown, one call per event — can still read
//               back in the right weapon's language.
export const JTAC = {
  enabled: true,
  volume: 0.5,              // base volume, further scaled by the user JTAC + master volumes
  fileExt: '.wav',          // one place to change if the clips are ever re-encoded
  assumedLength: 3.5,       // s — stand-in until a clip's real duration is known

  sampleInterval: 0.25,     // s between world samples (the director's tick rate)
  openingDelay: 4,          // s of dead air at mission start, before the net opens
  gap: { min: 1.4, max: 3.0 },  // s of dead air after every transmission
  queueTTL: 5,              // s a pending cue stays relevant (default; see `ttl`)
  maxQueue: 2,              // pending cues held at once — enough for a two-beat exchange
  preemptMargin: 40,        // priority a cue must beat the sounding one by to cut it off
  recentMemory: 3,          // clips remembered by the no-repeat guard

  // ── Tower ────────────────────────────────────────────────────────────────
  // An ordered script of one-shot clearances (see JTACRadio._updateTower). Each
  // step owns both halves of its own timing — the moment it goes out and the
  // moment it is retired unsent — which is what lets one script cover every
  // spawn pose without the scenarios knowing anything about it: a runway start
  // finds the taxi step already retired and opens on takeoff clearance, an
  // airborne start finds both ground steps retired and opens on the handoff.
  // Deliberately unbounded in time, unlike `checkIn`: a cold start can take as
  // long as the player likes to fumble through, and a clearance that arrives
  // when he is finally ready is still the right call.
  atc: {
    airborneAlt: 15,          // m AGL — latches "airborne", retiring the ground steps
    runwayMargin: 20,         // m of slack on the paved-runway test
    // The hold-short point in the runway frame (see training/runwayFrame.js) —
    // the taxiway/runway junction, with a radius generous enough that any taxi
    // route onto the runway passes through it, but tight enough to exclude the
    // parking spot 160 m away.
    holdShort: { along: -30.65, lateral: 60, radius: 100 },
    handoffDelay: 5,          // s airborne before tower hands off to the controller
    // ...and how long after that call finishes before the controller comes up.
    // The one knob that keeps the opening in order: tower, then targets.
    jtacDelay: 15,            // s
  },

  // ── Engagement geometry ──────────────────────────────────────────────────
  // An "engagement" is one pass at one cluster of JTAC-controlled targets
  // (anything that isn't a fast mover — air-to-air is not his business). It
  // opens when the player closes on a live target, carries the ingress script,
  // and resolves when the cluster is dead or the player egresses.
  engage: {
    openRange: 6000,        // m — a live target this close opens an engagement
    closeRange: 9000,       // m — ... and leaving this far from it breaks the engagement
    groupRadius: 1500,      // m — targets within this of the engagement's centre are "the group"
    assignRange: 5000,      // m — target designation, nose roughly on
    clearedHotRange: 3200,  // m — attack clearance, nose on
    nosedOn: 0.65,          // cos of the nose-to-target angle that counts as pointed at it
  },

  // ── Gun runs ─────────────────────────────────────────────────────────────
  // A run is bracketed by rounds leaving the gun, not by the trigger flag, so a
  // burst between two samples still registers. Its result picks the call.
  gunRun: {
    endDelay: 1.3,          // s without rounds that ends a run
    minRounds: 20,          // fewer than this is a twitch on the trigger, not a run
    dangerCloseAlt: 180,    // m AGL — pressing in below this earns the friendlies check
  },

  // ── Recovery ─────────────────────────────────────────────────────────────
  rtb: {
    range: 7000,            // m from the airfield
    altitude: 300,          // m AGL
    gearDown: 0.5,          // gear fraction that counts as committed to a landing
  },

  // ── Mission opening ──────────────────────────────────────────────────────
  // The controller's first words to the inbound flight, in order: he calls for
  // help, acknowledges the A-10 running in, then clears it into the area with
  // guns cold until he has something to designate. The beats are requested one
  // at a time — the next only once the previous has actually transmitted — so a
  // tactical call cutting in pauses the script instead of scrambling it.
  checkIn: {
    script: ['onStation', 'checkInAck', 'clearedIn'],
    // s after the controller takes the net (not after mission start — a cold
    // start and a taxi can put several minutes between those two) by which an
    // unsent remainder is dropped.
    window: 75,
    // ...and the other way the remainder is dropped: once a call this urgent
    // has gone out, the controller is working a target and the introductions
    // are over. Priority already encodes "this is tactical", so the test needs
    // no list to maintain — it sits above rtb (35) and the script's own beats
    // (30), below re-attack (45) and everything that outranks it.
    yieldPriority: 40,
  },

  // ── Idle chatter ─────────────────────────────────────────────────────────
  // Fills the lulls only: the net stays quiet while the player is shooting,
  // defending, or being talked to.
  chatter: {
    calmTime: 14,                   // s of quiet before chatter is allowed
    interval: { min: 32, max: 90 }, // s between chatter transmissions (re-rolled after every call)
  },

  cues: {
    // ── Tower (ordered; see `atc` above) ──
    // Priced above every tactical call so a clearance cannot be crowded off the
    // queue, and generous on TTL because a clearance is not time-critical the
    // way "cleared hot" is — it waits for the net rather than being binned.
    taxiClearance:    { net: 'atc', priority: 95, maxPlays: 1, ttl: 20, clips: ['01_taxi_clearance'] },
    takeoffClearance: { net: 'atc', priority: 95, maxPlays: 1, ttl: 20, clips: ['02_takeoff_clearance'] },
    contactJtac:      { net: 'atc', priority: 90, maxPlays: 1, ttl: 20, clips: ['03_contact_jtac'] },
    clearedHot:  { priority: 85, cooldown: 30,  clips: ['cleared_hot_01', 'cleared_hot_02'] },
    // One cue for every "that got them", whichever weapon got them: a gun run
    // and a Maverick impact seconds apart share the cooldown, so a kill is
    // called once. `gunClips` adds the gun-specific readbacks for a strafing
    // pass; the plain pool fits either weapon and is what a Maverick draws on.
    goodHits:    { priority: 75, cooldown: 12,
      clips: ['good_hits_01', 'good_hits_02', 'good_hits_03', 'good_hits_04'],
      gunClips: ['guns_good_01', 'guns_good_02',
        'good_hits_01', 'good_hits_02', 'good_hits_03', 'good_hits_04'] },
    stillHot:    { priority: 70, cooldown: 20,  clips: [
      'still_taking_fire_01', 'still_taking_fire_02', 'still_taking_fire_03',
    ] },
    assign:      { priority: 65, cooldown: 45,  maxPlays: 3, clips: ['put_guns_on_that_target'] },
    mark:        { priority: 60, cooldown: 90,  maxPlays: 2, clips: ['smoke_popped'] },
    // Two halves of the same beat, opposite ways round: the caution goes out as
    // the player presses in over the friendlies, the all-clear answers the run
    // once the rounds have stopped. The caution outranks it — it is only worth
    // anything before the trigger comes off.
    dangerCloseWarn: { priority: 72, cooldown: 90, maxPlays: 2, ttl: 4, clips: ['danger_close'] },
    dangerClose: { priority: 50, cooldown: 120, maxPlays: 2, ttl: 14, clips: ['friendlies_are_ok'] },
    reattack:    { priority: 45, cooldown: 40,  clips: ['put_guns_on_that_target', 'standby'] },
    rtb:         { priority: 35, cooldown: 60,  maxPlays: 1, clips: [
      'i_see_you_bring_her_in', 'bring_it_in',
    ] },
    // ── The opening exchange (ordered; see `checkIn` above) ──
    onStation:   { priority: 30, cooldown: 0, maxPlays: 1, clips: [
      'contact_we_need_your_help_down_here', 'hawg_overhead',
    ] },
    checkInAck:  { priority: 30, cooldown: 0, maxPlays: 1, clips: ['copy_approaching_from_west'] },
    clearedIn:   { priority: 30, cooldown: 0, maxPlays: 1, clips: ['cleared_for_combat_guns_cold'] },
    chatter:     { priority: 10, cooldown: 0,   clips: [
      'random_chatter_01', 'random_chatter_02', 'random_chatter_03',
      'random_chatter_04', 'random_chatter_05', 'random_chatter_06',
      'random_chatter_07', 'random_chatter_08', 'random_chatter_09',
      'random_chatter_10', 'random_chatter_11', 'random_chatter_12',
      'random_chatter_13', 'random_chatter_14', 'random_chatter_15',
      'random_chatter_16', 'random_chatter_17', 'random_chatter_18',
      'random_chatter_19', 'random_chatter_20', 'random_chatter_21',
    ] },
  },
};

// ── HUD ───────────────────────────────────────────────────────────────────
export const HUD = {
  canvasResolution: 1024,          // texture resolution (width & height)
  font: 'HornetDisplay, Consolas, monospace', // HUD typeface
  fontSize: 20,                    // base font size in canvas pixels
  color: '#00ff44',                // primary HUD green
  dimColor: '#009922',             // dimmer green for secondary elements
  pxPerDeg: 32,                    // canvas pixels per degree (pitch ladder / VV scale)
  hologramScale: 3,              // UV shift per metre of head offset (hologram parallax)
  // The parallax is faded out for an action mount (see CameraManager
  // .pilotEyeView) rather than cut, so pressing C from the cockpit doesn't
  // snap the symbology across the glass. 1/s on an exponential approach.
  hologramBlendRate: 5,
  // Velocity-vector caging — gear-bounce Vy over small forward speed makes atan2
  // noisy, so the VV is locked to boresight below vvCageMin and smoothsteps in
  // to fully live by vvCageMax.
  vvCageMin: 15,                   // m/s forward body speed (~30 kts)
  vvCageMax: 30,                   // m/s forward body speed (~60 kts)
  calibration: {
    offset: { x: 0, y: 0.4, z: 0 },     // positional nudge (model-local)
    rotation: { x: 0, y: 0, z: 0 },     // rotational nudge (radians)
    scale: { x: 1.5, y: 1.33 },         // UV scale multiplier
  },
  // AIM-9 seeker reticle — spins around the boresight cross while searching,
  // then animates onto the tracked target once locked. Drawn only in AIM-9 mode
  // with at least one missile mounted.
  aim9: {
    radiusPx: 15,           // circle radius (~1.5× velocity vector circle)
    lineWidth: 2.5,         // stroke width
    spinRadiusPx: 25,       // orbit radius around boresight cross while searching
    spinPeriod: 1.0,        // seconds per full orbit revolution
    transitionTime: 0.5,    // s — lerp duration on lock acquire/lose/switch
    lockSmoothing: 8,       // exp-decay rate (1/s) used during transitions
    // Angular calibration on both boresight + target screen pos, keeping search
    // and lock aligned with the gun bore cross. Offsets shift the reticle;
    // scales stretch the angular mapping around the boresight.
    pitchOffsetDeg: 0.5,      // + = raise reticle (toward horizon)
    yawOffsetDeg: 0,        // + = reticle to the right
    pitchScale: 1.18,        // multiplier on the pitch angle from boresight
    yawScale: 1.18,          // multiplier on the yaw angle from boresight
  },
  // Gun-mode depressible pipper (CCIP — continuously computed impact point)
  gun: {
    maxRange: 3000,          // m — hide pipper if impact is beyond this
    simStep: 0.04,           // s — integration step for bullet trajectory
    maxSimSteps: 160,        // safety cap on integration iterations
    radiusPx: 25,            // pipper dashed-circle radius (canvas px)
    dashCount: 8,            // number of dashes around the circle (exactly)
    dashFill: 0.5,           // fraction of each segment that is drawn (0–1)
    dotRadiusPx: 2,          // center dot radius (canvas px)
    lineWidth: 2.5,          // stroke width
    // Angular calibration — shift pipper from computed direction
    pitchOffsetDeg: 0.3,       // + = raise pipper (toward horizon)
    yawOffsetDeg: 0,         // + = pipper to the right
    // Velocity-vector blanking window — a screen square around the pipper the
    // VV is not drawn inside, so it can't overprint the aiming dot (as on the
    // real HUD, where the weapon symbol outranks the flight path marker).
    maskHalfPx: 20,          // half-size of the square (canvas px); 0 = off
    // ── Air-to-air funnel sight ─────────────────────────────────────────
    // Drawn in place of the CCIP pipper when no terrain solution exists. It
    // traces the in-flight bullet stream backwards through the rotating aircraft
    // frame, so it leans into a turn: pull lead until the target's wingtips
    // touch the funnel walls and rounds are on target.
    funnel: {
      targetWingspan: 7,   // m — calibration wingspan; rung half-width = wingspan / (2·range)
      numSegments: 8,        // line segments per side (samples = numSegments + 1)
      tofMin: 0.2,          // s — closest TOF sample (top, widest end of the funnel)
      tofMax: 1.4,           // s — farthest TOF sample (bottom, narrowest end)
      lineWidth: 2.5,        // stroke width (canvas px)
    },
  },
  // AGM-65 Maverick ground-aim reticle — a four-dash crosshair on the terrain
  // where the seeker points, using the same body-fixed projection as the gun
  // pipper so it tracks as the aircraft rolls. Drawn only in AGM-65 mode with a
  // Maverick mounted.
  maverick: {
    radiusPx: 25,            // crosshair circle radius (canvas px)
    dashFill: 0.5,           // fraction of each of the 4 arc segments that is drawn (0–1)
    dotRadiusPx: 2,          // center dot radius (canvas px)
    lineWidth: 2.5,          // stroke width
    // Angular calibration — shift the reticle from the computed seeker direction
    pitchOffsetDeg: 0.32,       // + = raise reticle (toward horizon)
    yawOffsetDeg: 0,         // + = reticle to the right
    maskHalfPx: 20,          // velocity-vector blanking square half-size (canvas px); 0 = off — see gun.maskHalfPx
  },
};

// ── In-game screen-space HUD overlay ──────────────────────────────────────
export const GAME_HUD = {
  // Enemy target highlighting (drawn while spacebar / freelook is held)
  target: {
    maxDistance: 27780,                                  // m (15 NM) — fade-out range
    diamondSize: 16,                                     // px (half-extent of diamond)
    color: '#ff8800',                                    // orange
    labelFont: '14px HornetDisplay, Consolas, monospace',
    minAlpha: 0.18,                                      // floor so distant marks remain visible
  },

  // Navigation waypoint markers — the spatial half of a training mission's
  // "move to waypoint" objective (TrainingMission feeds GameHUD the active set).
  // Cyan so it never reads as a target diamond. A waypoint outside the frustum
  // is pinned to the screen edge as a chevron pointing the way round.
  waypoint: {
    color: '#57e0ff',                                    // navigation cyan
    radius: 15,                                          // px — marker circle radius
    tick: 6,                                             // px — length of the four diagonal ticks
    lineWidth: 2,
    labelFont: '13px HornetDisplay, Consolas, monospace',
    rangeFont: '12px HornetDisplay, Consolas, monospace',
    edgeMargin: 52,                                      // px — inset of the off-screen chevron
    arrowSize: 12,                                       // px — off-screen chevron half-height
    // Pulse keeps the marker alive against a busy background.
    pulsePeriod: 1.6,                                    // s per breath
    pulseDepth: 0.25,                                    // 0–1 alpha swing
  },

  // Manual (mouse + keyboard) virtual stick: a small ring at bottom centre,
  // under the instructor captions and the command-feedback badge, with a dot
  // where the stick sits. Dim while centred, so it's there when you need to
  // find neutral and out of the way when you don't.
  stick: {
    color: '#9dff9d',
    radius: 18,                                          // px — full deflection
    bottomMargin: 10,                                    // px — ring's gap from the screen bottom
    dotRadius: 2.5,                                      // px
    lineWidth: 1.5,
    alphaIdle: 0.25,                                     // ring alpha with the stick centred
    alphaActive: 0.7,                                    // at full deflection
  },
};

// ── RWR Display (cockpit `_RWR` plane) ────────────────────────────────────
// The visual half of the radar warning receiver — the audio half is `RWR`
// above. Symbology is drawn to an offscreen canvas and mapped onto the `_RWR`
// plane in the HD cockpit, with the static artwork (rings + tick marks)
// composited over the top.
//
// Unlike a real ALR-69, where radial distance encodes received signal strength,
// this is a plain PPI scope: the centre is the aircraft, north is the nose, and
// a contact sits at its true relative bearing and slant range out to
// `GAME_HUD.target.maxDistance`. That makes it a cockpit-native alternative to
// the GameHUD target diamonds rather than a separate tactical picture.
//
// Every pixel dimension below is in canvas space at `canvasResolution`.
export const RWR_DISPLAY = {
  node: '_RWR',                   // plane mesh in the A-10 GLB
  overlayTexture: 'rwrDisplay',   // AssetManager texture key — static rings/ticks

  canvasResolution: 256,          // px — square offscreen canvas
  refreshRate: 2,                 // Hz — scope redraws per second (0 = every frame)
  maxRange: GAME_HUD.target.maxDistance, // m — contact at this range sits on `outerRadius`
  maxSymbols: 16,                 // clutter/perf cap; lowest-priority contacts drop first

  // The `_RWR` plane inherits glTF UVs, which put v=0 at the top — so the
  // canvas maps straight on with flipY off. Flip this if the scope reads
  // upside-down in the cockpit.
  flipY: false,

  // ── Scope geometry (fraction of the canvas half-width) ────────────────
  // `outerRadius` should line up with the artwork's outer ring; `centerHole`
  // keeps close-in contacts off the aircraft symbol at the centre.
  outerRadius:0.9,
  centerHole: 0.10,
  // Range → radius curve. 1 = true linear range (a contact's radius is its
  // actual distance). Below 1 pushes near contacts outward, giving more
  // separation in a knife fight; above 1 pulls them toward the centre.
  rangeExponent: 0.5,

  // ── Look ──────────────────────────────────────────────────────────────
  backgroundColor: '#08120c',     // unlit CRT face behind the symbology
  color: '#5fe39a',               // contact symbology (phosphor green)
  missileColor: '#5fe39a',        // in-flight missiles — set to `color` for a monochrome scope
  overlayColor: '#92c9aa',        // recolours the artwork; null draws the PNG as authored
  overlayAlpha: 0.7,
  screenBrightness: 1.0,          // multiplier on the whole screen (material colour)
  toneMapped: false,              // false keeps the phosphor punchy through ACES
  font: 'HornetDisplay, Consolas, monospace',

  // ── Symbols ───────────────────────────────────────────────────────────
  symbolSize: 24,                 // px — diamond half-diagonal
  primaryScale: 1.0,              // size multiplier on the highest-priority threat
  lineWidth: 1.75,                 // px
  fontSize: 24,                   // px — type code inside the diamond
  newestArcScale: 1.0,            // × symbolSize — semicircle capping the newest contact
  launchRingScale: 1.125,          // × symbolSize — ring around an emitter with a missile in the air
  missileRadius: 24,              // px — radius of the incoming-missile circle
  missileFontSize: 24,            // px — the "M" inside it
  blinkPeriod: 0.6,               // s per on/off cycle on missile symbols (0 = steady)

  // Fallback type code for an enemy whose params carry no `rwrCode`.
  defaultCode: '?',
};

// ── Flight-school training missions ───────────────────────────────────────
// Pacing + default tolerances for the guided training system (js/training).
// Mission content (objectives, waypoints, instructor lines) lives per-mission
// in js/training/missions/; this block only holds the knobs shared by all of
// them, so the whole system can be re-timed from one place.
export const TRAINING = {
  // Debug: draw every waypoint in the mission at once, from the first frame,
  // instead of only the active objective's. Purely visual — objectives still
  // advance one at a time — so the whole traffic pattern can be eyeballed and
  // tuned in place. Read per frame, so flipping it here takes effect on reload.
  debugWaypoints: false,

  // Instructor voicelines / captions.
  //
  // One recording covers a whole group of lines (an objective's briefing, its
  // completion credit, or a single hint), so the authored per-line `duration`
  // values act as *relative weights*: once the file's real length is known the
  // group is rescaled to fit it, and the captions track the voice. They're still
  // the literal on-screen times for any line whose audio is missing or hasn't
  // loaded yet, so they're worth keeping honest.
  caption: {
    gap: 0.35,          // s of silence between consecutive lines
    fade: 0.3,          // s — caption fade in/out at the ends of a run
    minDuration: 1.5,   // s — floor on a line's on-screen time
    volume: 1.0,        // instructor VO base volume, scaled by the user voice + master volumes
    tail: 0.25,         // s the last caption of a group lingers past the end of its audio
    maxStretch: 3,      // clamp on the authored→actual rescale, in case a file reads as garbage
  },

  // Objective flow.
  advanceDelay: 1.0,    // s between an objective completing and the next arriving
  completeHold: 1.1,    // s the ticked-green row lingers before it slides away
  startDelay: 3.0,      // s of quiet after the mission spawns, before the first line

  // Defaults for a `waypoint` objective that doesn't override them.
  waypoint: {
    radius: 500,        // m — horizontal capture radius (airborne legs)
    groundRadius: 30,   // m — capture radius for waypoints resolved on the ground
    vertical: 400,      // m — altitude band around the waypoint that still captures
  },

  // Defaults for an objective's assistive / corrective hint lines. A hint only
  // fires while the instructor is quiet, so nudges never stack on a briefing.
  hint: {
    after: 16,          // s the hint's condition must hold before it fires
    cooldown: 20,       // s before the same hint may fire again
    max: 3,             // times a single hint may fire per objective attempt
    duration: 3.5,      // s the hint caption stays up
  },
};

// ── First-run intro ───────────────────────────────────────────────────────
// A brand-new player's ENTER goes here instead of the start screen: a typed
// title card on black, a few slow shots of the parked A-10, then a glide into
// the cockpit that *is* the start of FT-101 (see ui/IntroSequence.js). FT-101
// spawns exactly where the start-screen preview parks, so the preview is
// promoted in place and the whole run is one continuous shot. Played until the
// player completes FT-101 or leaves a mission via Exit to Menu (core/FirstRun.js);
// `?intro` in the URL forces it regardless.
export const INTRO = {
  training: 'basicHandling',   // scenario (by its `training` key) the intro hands into
  lighting: 'dusk',            // LIGHTING key, pinned whatever the start screen holds

  // Title card — one string per line, typed left to right on black.
  title: [
    'April 3, 1992',
    'Barry M. Goldwater Air Force Range',
    'Yuma, Arizona',
  ],
  leadIn: 1.2,                 // s of black before the first character
  charInterval: 0.055,         // s per typed character (jittered ±charJitter)
  charJitter: 0.35,            // fraction of charInterval
  linePause: 0.5,              // s between lines
  hold: 2.4,                   // s the finished card stays up
  titleFadeOut: 0.9,           // s the text fades off the black
  titleGap: 0.6,               // s of empty black before the picture comes up

  // The intro's song, played once from the moment the last letter is typed and
  // left to run out under FT-101 (it fades itself out over its 108 s). Scaled by
  // the user's music + master volumes like the menu theme; faded out over
  // `fadeOut` if the player restarts or exits first.
  music: { file: 'assets/sound/Intro.wav', volume: 0.6, fadeOut: 2 },

  // Finishing the intro's FT-101 run: "MISSION COMPLETE" holds this long with
  // no continue prompt, then the player is taken to the start screen with the
  // next lesson (`nextMission`, by code) already selected.
  completeExitDelay: 6,
  nextMission: 'FT-102',

  // Per-character tick — the menu's switch sample, well under the click volume.
  tick: { file: 'assets/sound/Switch_01.wav', volume: 0.07, pitchJitter: 0.08 },

  revealFade: 2.2,             // s the first shot fades up from black
  cutFade: 0.45,               // s each side of the dip through black between shots
  audioFadeIn: 2.0,            // s the ambient bed comes up under the first shot (done before engineStart)
  // When the engines crank: `at` seconds into shot `shot` (0-based). FT-101's
  // own start-up step then finds them turning and skips itself; a skip of the
  // intro ahead of the cue starts them at the handoff instead.
  engineStart: { shot: 0, at: 2.5 },
  glideDuration: 2,          // s of the final glide from the last shot (~50 m out) into the cockpit
  skipFade: 0.6,               // s the black lifts when the player skips the card
  skipHintDelay: 1.5,          // s before the faint "ESC  SKIP" hint fades in

  // Shots, in aircraft-local metres (+X right wing, +Y up, −Z the nose; the
  // wheels sit ~1.2 m below the origin). Each moves the eye `from`→`to` and the
  // look-at `lookFrom`→`lookTo` over `duration`. `ease` is 'linear' (a dolly at
  // constant speed — what a hard cut wants) or 'out' (settles to rest, which the
  // last shot needs: the cockpit glide starts from a standstill). `focal` is the
  // lens in mm on a full-frame body, fitted to the frame's width (IntroCamera) —
  // long lenses from well back, for the compressed telephoto look.
  shots: [
    // Every shot holds the whole airframe (17.5 m span, 16.3 m long) with some
    // margin, so the standoff is set by the lens: ~45-55 m at 65-70 mm,
    // depending on how much of the span and length the angle shows at once.
    // All of them look at the airframe's middle (z ≈ +0.7) rather than a part.
    // Shots 1 and 3 are mirror images (left low, right high); all three travel
    // ~10-11 m in their 5 s, so the pace is the same from cut to cut.
    { // Down on the deck off the left side, arcing round toward the nose.
      duration: 6.0, focal: 65, ease: 'linear',
      from: [-32, -0.85, -38], to: [-23.5, -0.85, -44],
      lookFrom: [0, 0.3, -0.5], lookTo: [0, 0.3, -1.0],
    },
    { // Low rear quarter, drifting round behind the engines and twin tails.
      duration: 6.0, focal: 70, ease: 'linear',
      from: [-27, 0.1, 46], to: [-17, -0.1, 50],
      lookFrom: [0, 0.8, 1.0], lookTo: [0, 0.9, 1.5],
    },
    { // Rear-right quarter, swinging round to dead astern and settling into the
      // chase view's angle (above and behind, looking level down the nose) —
      // so the glide into the cockpit is a straight push forward, not a turn.
      // Held inside ~32 m (on a wider lens to keep the whole jet in frame):
      // the hangar behind the right quarter starts ~39 m out.
      duration: 6.0, focal: 40, ease: 'out',
      from: [17, 2.6, 25], to: [0, 3.2, 32.5],
      lookFrom: [0, 0.8, 0.5], lookTo: [0, 1.5, -10],
    },
  ],
};

// ── GAU-8/A Avenger ───────────────────────────────────────────────────────
export const GAU8 = {
  fireRateStart: 45,         // rounds per second at spin-up start
  fireRateMax: 60,           // rounds per second at full spin
  spinupTime: 1.0,           // seconds to reach max fire rate (and to spin down)
  muzzleVelocity: 1010,     // m/s
  ammoCapacity: 1150,        // rounds
  recoilForce: 45000,        // N — average recoil (~10,000 lbf) applied along -barrel
  dispersion: 5e-3,           // radians (5 mil) — 80th-percentile cone half-angle
  bulletMass: 0.39,          // kg (PGU-14/B API round)
  bulletDragCd: 0.3,         // drag coefficient (ogive projectile)
  bulletArea: 7.069e-4,      // cross-section m² (π × 0.015²)
  maxLifetime: 5.0,          // seconds — well beyond 1500 m effective range
  poolSize: 300,             // max simultaneous in-flight bullets
  trailMaxPoints: 200,       // max vertices per debug trail line
  debug: false,             // true = draw bullet trail lines regardless of global debug
  damage: 20,                // hit-point damage per round (mean)
  damageVariance: 2,         // ± Gaussian σ around mean damage
};

// ── AIM-9M Sidewinder ─────────────────────────────────────────────────────
export const AIM9 = {
  // Mounting (model node names in the A-10 GLB). Array order IS fire order:
  // far-left, far-right, inner-left, inner-right (outboard pairs first).
  railsMesh: '_Missile_Rails',
  pylonNodes: ['AIM-9_01', 'AIM-9_04', 'AIM-9_02', 'AIM-9_03'],  // double-rail (4), shows railsMesh
  singlePylonNodes: ['AIM-9_01_single', 'AIM-9_02_single'],      // single-rail (2), no railsMesh

  // Safe separation — guidance is inhibited within this distance (m) of the
  // launch position, so a missile can't steer into the launching aircraft.
  steerArmDistance: 20,

  // Physical (AIM-9M)
  mass: 85.5,                              // kg
  dragCd: 0.4,                             // slender-body drag coefficient
  bodyArea: Math.PI * 0.0635 * 0.0635,     // m² — body cross-section (5" diameter)

  // Motor (Mk 36 reduced-smoke solid rocket)
  thrust: 17800,                           // N (avg)
  burnTime: 4.0,                           // s

  // Acquisition / lock
  acquireRange: 3218.7,                    // m (≈ 2 mi)
  gimbalLimitDeg: 15,                      // ± from aircraft boresight

  // Guidance (proportional navigation + lead pursuit)
  navConstant: 4,                          // PN gain (typical 3–5)
  maxLateralAccel: 30 * 9.81,              // 30 g

  // Lifecycle
  maxLifetime: 15,                         // s
  fireInterval: 1.0,                       // s — minimum between shots
  proximityFuse: 8,                        // m — detonate on near-miss when closing

  // Damage
  damage: 100,                             // hp per direct hit / fuse
};

// ── AGM-65 Maverick ────────────────────────────────────────────────────────
// Air-to-ground TV/IR missile. Unlike the AIM-9 the seeker is player-aimed — a
// gimballed camera locked onto a ground point — and on fire it tracks either a
// nearby enemy vehicle or a static terrain point to impact. WeaponSystem owns
// the missile lifecycle; aim + TV rendering live in MaverickSeeker. Figures
// approximate the AGM-65D (IIR variant).
export const AGM65 = {
  // Mounting (model node names in the A-10 GLB), nose-forward like the AIM-9;
  // the flame mesh shows while the motor burns. Array order IS fire order —
  // alternating left/right so a partial load (sliced from the front) fills
  // symmetrically.
  railsMesh: '_Maverick_Rails',
  pylonNodes: ['AGM65_01', 'AGM65_04', 'AGM65_02', 'AGM65_05', 'AGM65_03', 'AGM65_06'],  // triple-rail (4 or 6), shows railsMesh
  singlePylonNodes: ['AGM65_01_Single', 'AGM65_02_Single'],                              // single-rail (2), no railsMesh
  flameMesh: 'AGM65_Flame',

  // Safe separation — guidance inhibited within this distance (m) of launch.
  steerArmDistance: 25,

  // Physical (AGM-65D)
  mass: 220,                               // kg
  dragCd: 0.4,                             // slender-body drag coefficient
  bodyArea: Math.PI * 0.1525 * 0.1525,     // m² — 12" (305 mm) body cross-section

  // Motor (Thiokol SR109/SR114 solid rocket) — short, hot boost then coast.
  thrust: 45000,                           // N (avg boost)
  burnTime: 3.2,                           // s

  // Guidance (proportional navigation + lead pursuit), softer than the AIM-9
  // since ground targets are slow or stationary.
  navConstant: 3.5,                        // PN gain
  maxLateralAccel: 12 * 9.81,              // 12 g — plenty for ground tracking

  // Lifecycle
  maxLifetime: 40,                         // s — long enough for an 8 mi lob
  fireInterval: 0.6,                       // s — minimum between shots
  proximityFuse: 6,                        // m — detonate on near-miss when closing

  // On fire, an enemy whose hit-volume centre falls within this radius of the
  // aim point is tracked as a moving target; otherwise the static ground point.
  lockAcquireRadius: 30,                   // m

  // Damage
  damage: 100,                             // hp per direct hit / fuse
};

// ── Maverick seeker + TV display ───────────────────────────────────────────
// Drives the gimballed seeker line-of-sight, the offscreen render of the TV
// feed, and the IR (white-hot) imaging look. The feed renders only while the
// Maverick weapon mode is selected and the cockpit view is active.
export const MAVERICK = {
  // Model node names (A-10 GLB)
  nodeTV: '_TV',                  // flat plane the camera feed renders onto
  nodeFPV: 'FPV_Maverick',        // pilot head pose while aiming (focused on TV)

  // Offscreen render target (square). The TV is a fuzzy low-res optic, so a
  // small target is both authentic and cheap.
  rtResolution: 512,

  // Seeker optic FOV (degrees) — narrow and magnified, with a player-controlled
  // zoom on the scroll wheel while aiming.
  fovDefault: 6,
  fovMin: 2,                      // most zoomed-in
  fovMax: 12,                     // most zoomed-out
  zoomStep: 0.85,                 // FOV multiplier per wheel notch (in = smaller FOV)
  zoomSpeed: 8,                   // FOV degrees/sec toward the target zoom (linear, not eased)

  gimbalLimitDeg: 30,             // half-angle the seeker can point off boresight

  // Mouse slew — radians the *target* aim point travels per pixel of motion at
  // the default FOV. The seeker doesn't jump to it, but pans toward it at
  // panSpeed (rad/s). Both scale by (currentFov / fovDefault) so the feel stays
  // 1:1 as the player zooms, which is what makes the slew read as mechanical.
  slewSensitivity: 0.0004,
  panSpeed: 0.1,

  // The target point must be within this range (and within gimbal) to fire.
  fireRangeMeters: 12875,         // m — 8 statute miles

  // Exp-decay blend rate (1/s) between the normal FPV head pose and the
  // FPV_Maverick "focused on the TV" pose.
  aimBlendRate: 6,

  // IR (white-hot) imaging look — see maverick_tv_frag.glsl. The world renders
  // to a dim grey base; enemies are re-rendered flat white on top.
  ir: {
    gain: 0.85,                   // brightness of the greyscale base (< 1 keeps terrain dim)
    gamma: 0.85,                  // tone curve on the base (1 = linear)
    coldFloor: 0.04,              // minimum brightness so deep shadows aren't pure black
    hotColor: [1.0, 1.0, 1.0],    // enemy white-hot colour
    scanlineStrength: 0.06,       // subtle horizontal scanline texture (0 = off)
    noiseStrength: 0.04,          // sensor grain
  },

  // Full-screen crosshair over the feed — four lines from the edges whose centre
  // gap closes when the target is in range.
  crosshair: {
    color: [1.0, 1.0, 1.0],
    lineWidthPx: 1,               // line thickness in TV-canvas pixels (of rtResolution)
    gapPx: 24,                    // half-gap from centre to each line tip (out of range)
    inRangeGapPx: 0,              // half-gap when in range (lines meet in the middle)
    gapBlendRate: 20,             // exp-decay rate the gap eases between the two
  },

  // Sight-line markings below centre on the vertical crosshair, as on the real
  // Maverick TV. Evenly spaced from the horizontal crosshair down to spanFrac of
  // the way to the bottom edge; length ≈ crosshair.gapPx.
  refLines: {
    color: [1.0, 1.0, 1.0],
    lineWidthPx: 1,               // line thickness (rtResolution px)
    count: 3,                     // number of evenly-spaced lines
    spanFrac: 0.5,                // lowest line at this fraction from centre → bottom edge
  },

  // Two-line cross pinned to the enemy the missile would actually track if fired
  // (nearest entity within AGM65.lockAcquireRadius of the aim point). Its world
  // position is re-projected each frame, so the marker follows the vehicle
  // across the feed.
  lockMarker: {
    color: [1.0, 1.0, 1.0],
    lineWidthPx: 1.5,             // arm thickness (rtResolution px)
    sizePx: 13,                   // half-length of each arm (no centre gap)
  },
};

// ── Generic Enemy Missile (heat-seeker) ───────────────────────────────────
// Fired by AI platforms at the player. Mirrors the AIM-9M — same airframe-class
// physics + PN guidance — but uses the plain `missile.glb` (no flame mesh) and
// isn't pre-mounted: rounds spawn from the launcher's origin at fire time.
export const ENEMY_MISSILE = {
  model: 'missile',                        // AssetManager key for the missile GLB

  // Safe-separation arming distance (m from launch pos)
  steerArmDistance: 30,

  // Physical
  mass: 85.5,                              // kg — same class as AIM-9M
  dragCd: 0.4,
  bodyArea: Math.PI * 0.0635 * 0.0635,     // m² (5" body)

  // Motor
  thrust: 17800,                           // N (avg)
  burnTime: 4.0,                           // s

  // Guidance
  navConstant: 4,
  maxLateralAccel: 30 * 9.81,              // 30 g

  // Countermeasure decoy — while the target dispenses flares, the spoofed
  // seeker chases a point this far behind the aircraft along its flight path,
  // so it slides off the tail and misses. A cheap stand-in for tracking flares.
  flareDecoyDistance: 20,                  // m behind the target

  // Lifecycle
  maxLifetime: 15,                         // s
  proximityFuse: 4,                        // m

  // hp per hit. Aircraft.onProjectileHit ignores the value for the player
  // (strikes are counted instead); other targets wire it in directly.
  damage: 100,

  // ── AI carriage / firing ────────────────────────────────────────────────
  magazine: 4,                             // missiles per platform
  salvoCooldown: 12.0,                     // s between successive launches
  salvoCooldownRandom: 6.0,                // ± random variance on cooldown (s)
  minRange: 400,                           // m — too close to bother
  maxRange: 3000,                          // m — outside this, hold fire
  // Target aspect cone (half-angle, degrees) — the angle between the player's
  // velocity and the bearing player → launcher. Small aspect is head-on, near
  // 180° is the rear quarter; either favours a heat-seeker shot.
  aspectConeDeg: 20,

  // Seeker gimbal half-angle from the launcher's body-forward axis. The IR
  // seeker aligns with the launcher's nose at launch, so a target outside this
  // cone can't be acquired and the launch is suppressed.
  gimbalLimitDeg: 25,

  // Fire-time mesh orientation correction (radians), multiplied into the spawn
  // quaternion so a GLB whose nose isn't along -Z still points along velocity.
  spawnRotationEuler: [0, 0, 0],

  // Unused by AI firing decisions (which gate on geometry/cooldown) — kept for
  // parity with the AIM-9 config.
  acquireRange: 4500,
};

// ── Particles ─────────────────────────────────────────────────────────────
export const PARTICLES = {
  maxCount: 8192,            // total pool size (shared across both meshes)
  atlasSize: 4,              // 4×4 grid = 16 tile slots
  nearFadeStart: 2.0,        // view-space distance where alpha begins fading to 0
  nearFadeEnd:   5.0,        // view-space distance where alpha reaches full

  // Tumbling shrapnel for spawnExplosion — N flat random polygons pre-baked at
  // startup, each an InstancedMesh sharing one material.
  debris: {
    poolSize: 256,           // total live debris pieces across all explosions
    shapeCount: 8,           // pre-baked random polygon variants
    gravity: 9.81,           // m/s² — falls under gravity
    drag: 0.5,               // air drag (1/s) — fluttering deceleration
    angularDrag: 0.3,        // angular drag (1/s) — tumble decay
    color: [0.18, 0.16, 0.14], // base charred color (linear RGB)
    colorJitter: 0.12,       // ± per-instance brightness variation
  },

  // Scattered ground dust for a blast sitting on the deck
  // (spawnGroundDustBurst) — the patch of dirt a ground vehicle's death
  // explosion lifts around itself, on top of the single central kick-up
  // spawnExplosion already does.
  groundDustBurst: {
    count: 12,              // puffs per burst
    radius: 16,             // m — max scatter distance from the blast
    riseHeight: 1.5,        // m — random spawn height above the ground
    riseSpeed: [1.0, 3.5],  // m/s — upward kick
    outwardSpeed: 6,        // m/s — outward drift at the seat of the blast, fading to 0 at the rim
    jitterSpeed: 0.8,       // m/s — ± horizontal randomisation on top of the outward drift
    sizeScale: [0.35, 0.7], // fraction of the dustLarge default size
  },

  // Per-type defaults. [min, max] ranges are randomized at spawn; emissive > 0
  // routes the particle to the additive mesh.
  types: {
    flash:     { tile: 5,  lifetime: [0.05, 0.1],startSize: [3, 6],    endSize: [8, 12],   startAlpha: 0.5, endAlpha: 0, friction: 1.0,  drift: false, emissive: 3.0, gravity: 0,    color: [1, 0.95, 0.8] },
    sparks:  { tile: 2,  lifetime: [0.1, 0.2], startSize: [1, 2],    endSize: [10, 15],   startAlpha: 1.0, endAlpha: 0, friction: 0.85, drift: false, emissive: 1.5, gravity: 0,    color: [1, 0.7, 0.3] },
    dustSmall: { tile: 0,  lifetime: [2.0, 4.0], startSize: [2, 4],    endSize: [6, 12],   startAlpha: 0.8, endAlpha: 0, friction: 0.95, drift: true,  emissive: 0,   gravity: 0, color: [0.75, 0.69, 0.57] },
    dustLarge: { tile: 4,  lifetime: [5.0, 8.0], startSize: [4, 8],    endSize: [24, 48],   startAlpha: 0.35, endAlpha: 0, friction: 0.95, drift: true,  emissive: 0,   gravity: 0, color: [0.75, 0.69, 0.57] },
    muzzleSmokeA: { tile: 0,  lifetime: [0.60, 0.12], startSize: [2, 4],    endSize: [12, 18],   startAlpha: 0.4, endAlpha: 0, friction: 0.95, drift: true,  emissive: 0,   gravity: 0, color: [1, 1, 1] },
    muzzleSmokeB: { tile: 4,  lifetime: [0.80, 0.16], startSize: [1, 2],    endSize: [16, 24],   startAlpha: 0.04, endAlpha: 0, friction: 0.95, drift: true,  emissive: 0,   gravity: 0, color: [1, 1, 1] },
    whiteSmoke: { tile: 4,  lifetime: [6.0, 12.0], startSize: [2, 4],    endSize: [12, 24],   startAlpha: 0.1, endAlpha: 0, friction: 0.0, drift: true,  emissive: 0,   gravity: 0, color: [0.9, 0.9, 0.9] },
    blackSmoke: { tile: 4,  lifetime: [6.0, 12.0], startSize: [2, 4],    endSize: [12, 24],   startAlpha: 1, endAlpha: 0, friction: 0.0, drift: true,  emissive: 0,   gravity: 0, color: [0.03, 0.03, 0.03] },
    fire: { tile: 1,  lifetime: [0.5, 1.0], startSize: [1, 3],    endSize: [3, 8],   startAlpha: 0.8, endAlpha: 0, friction: 0.95, drift: true,  emissive: 10,   gravity: 0, color: [1, 0.549, 0] },
    // Ground-fire variants (spawnGroundFireSmokeTrail) for wrecks burning on the
    // terrain. Negative gravity gives the buoyant rise of a heat plume.
    groundFire:  { tile: 1,  lifetime: [0.8, 1.4],   startSize: [1.5, 3],  endSize: [4, 9],   startAlpha: 0.9, endAlpha: 0, friction: 1.0, drift: true,  emissive: 10,   gravity: -3.0, color: [1, 0.549, 0] },
    groundSmoke: { tile: 4,  lifetime: [8.0, 14.0],  startSize: [2, 4],    endSize: [14, 28], startAlpha: 1,   endAlpha: 0, friction: 0.995,  drift: true,  emissive: 0,   gravity: -3.0, color: [0.03, 0.03, 0.03] },

    flare:     { tile: 5,  lifetime: [0.02, 0.02],startSize: [2.5, 2.5],    endSize: [2.5, 2.5],   startAlpha: 1, endAlpha: 0, friction: 0.95,  drift: true, emissive: 32.0, gravity: 0,    color: [1, 0.812, 0.250] },
  },
};

// ── Sprite-Sheet Explosions ───────────────────────────────────────────────
// Filmed explosions, played back as a camera-facing billboard by
// fx/ExplosionSystem. They run *on top of* the ParticleSystem blast, not
// instead of it: the particles carry the debris, dust and lingering smoke
// column, the billboard carries the fireball's shape and motion, which no
// amount of puffs reproduces.
//
// Adding a clip is two steps and no code:
//   1. node tools/build-explosion-sheet.mjs --src <frames> --out public/assets/fx/<name>.png
//   2. paste the entry it prints into `animations` below, and point an event at it.
export const EXPLOSION_SPRITES = {
  nearFadeStart: 3.0,   // view-space distance where alpha begins fading to 0
  nearFadeEnd:  10.0,   // view-space distance where alpha reaches full

  // One entry per clip. `file` is what AssetManager loads (keyed by the entry
  // name); the rest describes the sheet the build script produced and is
  // printed verbatim when it runs.
  animations: {
    simple15: {
      file:   'fx/explosion_simple_15.png',
      cols:   12,
      rows:   9,
      frames: 108,
      fps:    30,
      // Ignition point in tile UV, origin bottom-left — measured from the
      // first frame's matte. The quad hangs off this point, so the blast
      // climbs out of the ground instead of being centred on it.
      anchor: [0.457, 0.059],
      size:   30,   // quad edge in metres at size multiplier 1
      pool:   6,    // max concurrent playbacks of this clip
      mirror: true, // flip horizontally at random, so repeats don't read as copies
      // Fire mask thresholds, evaluated in LINEAR light (the sheet is uploaded
      // as sRGB, so the GPU decodes it on sample). `warmth` scales red-minus-
      // blue into the mask, which is what catches the dark orange skirt that
      // luminance alone misses; `emissive` is the bloom-feeding boost applied
      // to whatever the mask selects.
      fire: { lumLow: 0.10, lumHigh: 0.45, warmth: 1.5, emissive: 1.2 },
    },
  },

  // Game events → the clip(s) that serve them. `animation` may be an array, in
  // which case one is picked at random per spawn — which is how a second clip
  // for the same event becomes a one-line change.
  events: {
    // A Maverick killing a ground vehicle. Deliberately narrow: the billboard
    // is a big, authored, ground-anchored fireball, and it only reads right on
    // a vehicle brewing up on the deck under a 125 kg warhead.
    vehicleMaverickKill: { animation: ['simple15'], size: 1.0 },

    // An enemy airframe going into the deck. Larger than the vehicle blast —
    // it's a fuelled aircraft arriving at speed, and the particle explosion
    // under it is scaled up to match.
    aircraftGroundImpact: { animation: ['simple15'], size: 1.35 },
  },
};

// ── Countermeasure Flares ─────────────────────────────────────────────────
// Cosmetic-only IR decoys dispensed from the A-10's `_Flares_Left` /
// `_Flares_Right` nodes. Each live flare integrates its own ballistic
// trajectory, emits `flare` particles from PARTICLES.types, and owns a `flare`
// trail from SMOKE_TRAILS.presets.
export const FLARES = {
  poolSize: 32,                 // max simultaneous live flares
  capacity: 240,                // total carried with a 'full' loadout (unless unlimited)
  dispenserNodes: ['_Flares_Left', '_Flares_Right'],  // one flare each per interval
  dispenseInterval: 0.65,       // seconds between successive dispenses while held
  burnTime: [3.0, 5.0],         // [min, max] seconds — random per flare
  ejectSpeed: 20.0,             // m/s along aircraft body -up (downward)
  ejectSpread: 3.0,             // m/s — random per-axis kick so they fan out
  dispenseJitter: [0.0, 0.15],  // [min, max] s of extra delay per node, so they stagger
  drag: 0.6,                    // 1/s — exponential decay of air-relative velocity
  // AI flare bursts (enemy MiGs and Mi-8s). Cadence inside a burst matches
  // `dispenseInterval`.
  ai: {
    reactionDelay: [0.3, 0.8],  // [min, max] seconds — pilot reaction before 1st flare
    burstCount:    [4, 6],      // [min, max] dispense events in a single burst
  },
};

// ── Smoke Trails ──────────────────────────────────────────────────────────
// Ribbon-mesh trails for fast-moving emitters, each one slot in a fixed pool of
// pre-allocated camera-facing strips. Far cheaper than hundreds of billboarded
// particles per missile, and the ribbon stays continuous regardless of speed/fps.
export const SMOKE_TRAILS = {
  poolSize: 64,           // max simultaneous emitters
  maxPoints: 256,         // spine points per emitter — caps length at maxPoints × pointSpacing
  nearFadeStart: 2.0,     // view-space distance where alpha begins fading to 0
  nearFadeEnd:   5.0,     // view-space distance where alpha reaches full

  // fBm drift field perturbing anchors each frame — a smooth, spatially
  // coherent alternative to per-anchor white noise. Per-preset driftJitter
  // scales the amplitude.
  drift: {
    spatialFrequency: 0.005, // 1/m — base-octave variation across space (higher = tighter waviness)
    timeFrequency:    0.2,   // Hz — how quickly the field evolves
    octaves:          3,     // fBm octaves (>= 2), lacunarity 2 / gain 0.5
  },

  // Per-use-case presets — passed to SmokeTrailSystem.spawn() at fire time.
  presets: {
    aim9: {
      color: [0.9, 0.9, 0.9],
      startOpacity: 0.35,
      endOpacity: 0,
      startWidth: 0.6,
      endWidth: 24,
      length: 6.0,           // seconds the trail persists behind the emitter
      pointSpacing: 16,      // m between spine points (curve resolution)
      textureLength: 96,     // m per full vertical tile of smoke_trail.jpg
      emissive: 0,
      drift: true,
      driftJitter: 3.0,      // m/s — fractal-noise turbulence added to wind, for organic dispersion
    },
    flare: {
      color: [0.9, 0.9, 0.9],
      startOpacity: 0.5,
      endOpacity: 0,
      startWidth: 1,
      endWidth: 20,
      length: 6.0,
      pointSpacing: 6,
      textureLength: 96,
      emissive: 0,
      drift: true,
      driftJitter: 3.5,
    },
    agm65: {
      color: [0.85, 0.85, 0.85],
      startOpacity: 0.35,
      endOpacity: 0,
      startWidth: 0.8,
      endWidth: 24,
      length: 7.0,
      pointSpacing: 16,
      textureLength: 96,
      emissive: 0,
      drift: true,
      driftJitter: 3.0,
    },
  },
};

// ── Aircraft Ground Shadow ────────────────────────────────────────────────
// Cosmetic ground AO blob — a flat plane on the terrain, alpha-mapped with
// shadow.png, oriented to aircraft heading only (terrain slope ignored).
export const SHADOW = {
  size: 25,                  // meters (square plane edge)
  yOffset: 0.195,            // meters above terrain to prevent z-fighting
  strength: 0.7,             // overall opacity multiplier (0–1)
  fadeStartAGL: 0,           // m AGL — full strength up to here
  fadeEndAGL: 10,            // m AGL — fully gone past this
};

// ── Cockpit Instruments ───────────────────────────────────────────────────
// Physical gauges that rotate / show-hide from aircraft state, updated only
// while the cockpit camera is active (or transitioning to it).
//
// Per-needle config:
//   mesh              — Object3D name in the GLB
//   axis              — local rotation axis ('x' | 'y' | 'z')
//   direction         — ±1, flipping the sign for how the mesh was authored
//   zeroAngleDeg      — rotation offset (deg) at value = 0
//   unitsPerRotation  — indicated quantity per full 360° needle revolution
//   stiffness/damping — optional spring override; k pulls to target, c resists
//                        overshoot (critical damping ≈ 2·√k)
//
// The attitude ball spring-damps pitch and roll independently; the gear
// indicator is a visibility toggle with no rotation.
export const INSTRUMENTS = {
  // ── Default spring-damper (bouncy: damping ratio ≈ 0.35) ────────────────
  stiffness: 80,
  damping: 6,

  airspeed: {
    mesh: '_ins_airspeed',
    axis: 'y',
    direction: -1,              // clockwise
    zeroAngleDeg: -90,
    unitsPerRotation: 600,      // knots per full rev
  },

  airspeedMax: {
    mesh: '_ins_airspeed_max',
    axis: 'y',
    direction: -1,
    zeroAngleDeg: -90,
    unitsPerRotation: 600,      // knots per full rev (same dial as airspeed)
    maxKts: 364,                // clean configuration Vne
    maxKtsDirty: 185,           // gear or flaps extended
  },

  altitude: {
    mesh: '_ins_altitude',
    axis: 'y',
    direction: -1,
    zeroAngleDeg: -90,
    unitsPerRotation: 10000,    // feet per full rev
  },

  aoa: {
    mesh: '_ins_aoa',
    axis: 'y',
    direction: -1,               // clockwise
    zeroAngleDeg: 0,
    unitsPerRotation: 40,       // degrees of AoA per full rev
    offsetDeg: -2,              // calibration — added to raw AoA (deg) before display
  },

  climb: {
    mesh: '_ins_climb',
    axis: 'y',
    direction: -1,              // clockwise when climbing
    zeroAngleDeg: 0,
    unitsPerRotation: 12000,    // ft/min per full rev (180° = 6,000 ft/min)
    maxRotationDeg: 360,        // total needle travel — clamps to ±(maxRotationDeg/2)
  },

  gmeter: {
    mesh: '_ins_gmeter',
    axis: 'y',
    direction: -1,              // clockwise under positive G
    zeroAngleDeg: 0,            // needle rest pose already reads 0 G
    // Dial face: 1 G at 9 o'clock, 10 G at 3 o'clock (180° = 9 G → 20°/G).
    unitsPerRotation: 18,       // G per full rev
    // Travel clamp is symmetric about the rest pose, so the needle pins at
    // ±10 G rather than wrapping past the dial on a contact/impact spike.
    maxRotationDeg: 400,
    // A gimbaled accelerometer mass: quick enough to show the peak of a pull,
    // damped enough not to ring on the per-frame noise in ground-contact
    // forces (ζ ≈ 0.48 vs the default 0.34).
    stiffness: 70,
    damping: 8,
  },

  flaps: {
    mesh: '_ins_flaps',
    axis: 'y',
    direction: -1,
    zeroAngleDeg: 180,
    fullDeployDeg: 90,          // needle follows aircraft.flaps (0–1) continuously
    // Slower / less bouncy than the needle gauges
    stiffness: 60,
    damping: 8,
  },

  attitudeBall: {
    mesh: '_ins_attitude_ball',
    // The ball rotates opposite to aircraft attitude, so the horizon line
    // appears to stay with the world.
    pitchAxis: 'x',
    pitchDirection: 1,
    rollAxis: 'z',
    rollDirection: -1,
    // Calibration — added to raw pitch (deg) before display. The bezel faces
    // the pilot tilted ~17° back and sits above the ball's equator, so the
    // sight line through the aircraft symbol meets the ball well above it;
    // this rotates the ball's zero line back onto that sight line.
    pitchOffsetDeg: -11,
    // Heavier / less bouncy than needle gauges (a gimbaled ball has mass)
    stiffness: 50,
    damping: 10,
  },

  altHundred: {
    mesh: '_ins_alt_hundred',
    axis: 'x',
    direction: -1,
    zeroAngleDeg: 0,
    unitsPerRotation: 1000 * 3, // feet per full rev
    // Drums are direct-drive — stiff and heavily damped, so the reading tracks
    // altitude with no overshoot.
    stiffness: 400,
    damping: 40,
  },

  altThousand: {
    mesh: '_ins_alt_thousand',
    axis: 'x',
    direction: -1,
    zeroAngleDeg: 0,
    unitsPerRotation: 10000 * 3,   // feet per full rev
    stiffness: 400,
    damping: 40,
  },

  headingCompass: {
    mesh: '_ins_heading_compass',
    axis: 'y',
    direction: -1,
    zeroAngleDeg: 0,            // compass-card offset at heading = 0° (North)
    unitsPerRotation: 360,      // degrees of heading per full rev
  },

  gear: {
    mesh: '_ins_gear',
    threshold: 0.99,            // mesh hidden below this gear position (0 = up, 1 = down+locked)
  },
};

// ── Cockpit Controls ──────────────────────────────────────────────────────
// Pilot-operated hardware animated from aircraft inputs: the throttle slides,
// the stick rotates on two axes, the gear lever flips on toggle. Throttle and
// stick use the INSTRUMENTS spring-damper for a touch of "hand mass"; the gear
// lever is linear, since flip levers have detents and shouldn't overshoot.
//
// Per-control config:
//   mesh              — Object3D name in the GLB
//   axis              — local rotation/translation axis ('x' | 'y' | 'z')
//   direction         — ±1, flipping the sign for how the mesh was authored
//   stiffness/damping — optional per-control override of the defaults
export const COCKPIT_CONTROLS = {
  // ── Default spring-damper (slightly stiffer than instruments) ───────────
  stiffness: 120,
  damping: 16,

  throttle: {
    mesh: '_control_throttle',
    axis: 'z',                     // local axis to slide along
    direction: -1,                 // sign so throttle = 1 pushes lever forward
    travel: 0.9,                   // meters from idle to full
  },

  stick: {
    mesh: '_control_stick',
    pitchAxis: 'x',
    pitchDirection: 1,             // + elevator → stick back (toward pilot)
    pitchMaxDeg: 10,               // stick travel at full elevator deflection
    rollAxis: 'z',
    rollDirection: 1,             // + ailerons → stick right
    rollMaxDeg: 10,                // stick travel at full aileron deflection
  },

  gear: {
    mesh: '_control_gear',
    axis: 'x',
    direction: -1,
    deployedAngleDeg: 28,          // rotation when gear = 1 (down)
    rate: 4,                        // units/second linear chase (full flip ≈ 0.25s)
  },
};

// ── Pilot Head ────────────────────────────────────────────────────────────
// Procedural head animation for the seated pilot (`_Cockpit_SD`). The pilot is
// only drawn in the external views, so this never costs anything from inside
// the cockpit — see cockpit/PilotHead.js.
//
// The model is a single scalar — `arousal` (0..1) — summed from how dangerous
// the current second of flight is, plus a gaze state machine whose dwell times
// *compress* as arousal rises. That's what makes a calm cruise read as the odd
// unhurried glance at a gauge and a gunfight as a head that never stops
// working, without two separate behaviour sets.
//
// Look angles are authored as yaw/pitch in the aircraft body frame, where
// +yaw is to the pilot's left and +pitch is up. The instrument values below
// are measured from the head node to the real `_ins_*` meshes in the GLB.
export const PILOT_HEAD = {
  mesh: '_Cockpit_Pilot_Head',

  // Neck hinge, as fractions of the head mesh's own bounding box (0 = its
  // underside / its front face). The mesh origin sits up near the crown, which
  // would swing the chin through the canopy; the real atlanto-occipital joint
  // is low and well aft of centre.
  // Measured against the model: 0.36 / 0.5 puts the hinge at world y 0.92,
  // mid-depth through the helmet — 8 cm below and 18 cm behind the FPV eye
  // point, and just above the pilot mesh's shoulders.
  pivotHeightFrac: 0.36,
  pivotDepthFrac: 0.5,

  // Gaze is split between neck and eyes: the head only carries this much of the
  // angle to a look point and the eyes cover the rest, which is why a glance at
  // a gauge 27° down only pitches the head ~19°.
  headFollow: 0.7,

  // Range of motion (deg) — helmeted, shoulder-harnessed, looking over a seat.
  yawLimitDeg: 78,
  pitchUpLimitDeg: 30,
  pitchDownLimitDeg: 45,
  rollLimitDeg: 22,

  // Saccade spring. Stiffness lerps calm → alert so an alarmed head snaps; the
  // damping ratio sits just under critical for a human overshoot-and-settle.
  stiffnessCalm: 26,
  stiffnessAlert: 90,
  dampingRatio: 0.82,

  // How often the world is re-examined (s) for the nearest enemy / inbound
  // missile. Tracking an already-chosen target is per-frame; only the *choice*
  // is throttled, so a 200-vehicle convoy costs a quarter-second scan, not a
  // per-frame one.
  scanInterval: 0.25,

  // ── Arousal ─────────────────────────────────────────────────────────────
  // Terms are summed then clamped to 0..1. Rise is fast (adrenaline), fall is
  // slow (a pilot stays wound up for a few seconds after the shooting stops).
  arousal: {
    rise: 3.0,                  // per-second approach when climbing
    fall: 0.3,                  // per-second approach when settling
    perG: 0.22,                 // per G of |n - 1|
    perRadSec: 0.55,          // per rad/s of combined body pitch+roll rate
    stall: 0.35,                // alpha past stallWarnFrac of the stall angle
    stallWarnFrac: 0.8,
    firing: 0.45,               // gun trigger down
    maverick: 0.3,              // slewing the Maverick seeker
    threat: 1.0,                // missile inbound at the player — pins arousal
    enemy: 0.55,                // nearest live enemy, scaled by proximity
    enemyFullRange: 700,        // m — full `enemy` term inside this
    enemyFadeRange: 3000,       // m — no contribution beyond this
    lowAltitude: 0.3,           // scaled 0 → 1 as AGL falls to zero
    lowAltitudeAGL: 180,        // m — "pressing it" floor
  },

  // ── Gaze scheduling ────────────────────────────────────────────────────
  // Dwell ranges are [min, max] seconds; the forward/track ones are lerped
  // calm → alert, which is the main dial on how busy the head looks. Weights
  // are relative to forward = 1 and decide *where* a glance goes.
  glance: {
    forwardDwellCalm: [2.6, 6.5],
    forwardDwellAlert: [0.5, 1.4],
    trackDwellCalm: [1.4, 3.0],
    trackDwellAlert: [0.8, 2.2],
    instrumentDwell: [0.45, 1.0],   // a gauge is read, not studied
    scanDwell: [0.7, 1.8],
    threatDwell: [0.6, 1.2],        // re-picked often so the gaze stays fresh
    threatPanelChance: 0.25,        // ...and sometimes breaks to the scope
    threatForwardChance: 0.2,       // ...or back out front

    // Weights are relative to forward = 1. The alert instrument weight is the
    // *higher* of the two on purpose: the compressed dwells already mean more
    // picks per minute, and the glance rate at the panel has to rise with them
    // rather than get crowded out by the bandit.
    instrumentsWeightCalm: 0.6,
    instrumentsWeightAlert: 0.9,
    scanWeightCalm: 0.35,
    scanWeightAlert: 0.3,
    trackWeightCalm: 0.8,           // something is out there but it's quiet
    trackWeightAlert: 1.6,          // eyes mostly on the bandit

    forwardDriftDeg: 6,             // idle wander about the forward field
    forwardDriftPitchDeg: 4,
    scanYawDeg: [22, 70],           // magnitude; side is picked at random
    scanPitchDeg: [-12, 18],
    checkSixArousal: 0.5,           // arousal above which a scan may be a check-six
    checkSixChanceAlert: 0.35,      // ...and the chance it is one
    checkSixPitchDeg: 8,
    instrumentJitterDeg: 1.5,
  },

  // Panel look points, measured from the head node to the instrument meshes.
  instrumentLooks: [
    { yawDeg: 1.1,  pitchDeg: -25.2 },   // attitude ball
    { yawDeg: 9.7,  pitchDeg: -27.1 },   // airspeed
    { yawDeg: -7.5, pitchDeg: -27.3 },   // altimeter
    { yawDeg: 1.2,  pitchDeg: -34.6 },   // heading compass
    { yawDeg: -7.3, pitchDeg: -21.8 },   // VSI
  ],
  lookRWR:  { yawDeg: 9.5,  pitchDeg: -21.3 },   // threat scope
  lookTV:   { yawDeg: -15.4, pitchDeg: -24.8 },  // Maverick TV
  lookGear: { yawDeg: 29.5, pitchDeg: -30.5 },   // gear / flap indicators
  rwrChanceThreat: 0.6,      // chance a panel glance goes to the scope under threat
  gearChanceMoving: 0.7,     // chance it goes to the gear lights while they cycle

  // Look into the turn — a little yaw with the body yaw rate, the way a pilot
  // leads a bank. Suppressed while locked onto something.
  turnLeadGain: 1.1,         // rad of head yaw per rad/s of body yaw rate
  turnLeadMaxDeg: 14,

  // ── Involuntary motion ──────────────────────────────────────────────────
  // Sum-of-sines micro-movement: breathing and buffet when calm, a restless
  // head when alert. Cheaper than noise fields and good enough at this scale.
  noise: {
    ampCalmDeg: 0.18,
    ampAlertDeg: 0.77,
    freqCalm: 0.7,             // rad/s base
    freqAlert: 3.4,
  },

  // Sustained G drags the helmet down and lateral G tips it away from the
  // acceleration; high G also pins the neck, shrinking the usable range so the
  // head locks forward and strains instead of swivelling freely.
  gSmoothing: 8,             // per-second lag on the G read (matches the camera's)
  gSagDegPerG: 3.2,
  gSagMaxDeg: 11,
  gRollDegPerG: 7,
  rollWithYaw: 0.18,         // head tips into whatever it turns to look at
  strainStartG: 2.5,
  strainFullG: 6.0,
  strainMinRange: 0.3,       // fraction of the neck limits left at full strain

  // Dead pilot: the head drops and stays dropped.
  limp: { yawDeg: -14, pitchDeg: -40, rollDeg: 16, stiffness: 7 },
};

// ── Enemy Helicopters ─────────────────────────────────────────────────────
// Per-type kinematic flight params for AI helicopters. Add new types here.
export const HELICOPTERS = {
  mi8: {
    model: 'mi8',                    // AssetManager key
    // RWR scope identity — the code drawn inside the threat diamond, and the
    // lethality rank that decides which contact is the primary threat (highest
    // wins, ties broken by range). See RWR_DISPLAY.
    rwrCode: 'H',                    // Hip — helicopter
    rwrPriority: 1,
    rotors: {
      main: { mesh: 'Rotor_Main', axis: 'y' },
      tail: { mesh: 'Rotor_Tail', axis: 'y' },
    },
    cruiseSpeed: 55,                 // m/s (~107 kt)
    maxSpeed: 70,                    // m/s
    minSpeed: 0,                     // can hover
    maxClimbRate: 8,                 // m/s
    maxDescentRate: 6,               // m/s
    forwardAccel: 4,                 // m/s²
    verticalAccel: 3,                // m/s²
    maxYawRate: 20 * DEG2RAD,        // rad/s
    yawAccel: 25 * DEG2RAD,          // rad/s² — angular accel cap on yaw rate
    yawRateGain: 1.5,                // 1/s — heading-error → desired yaw rate (capped at maxYawRate)
    forwardLeanMax: 14 * DEG2RAD,    // visual nose-down at full speed
    bankPerYawRate: 0.5,             // visual roll = yawRate * this (radians per rad/s)
    // Spring-damper on visual pitch / roll (period ≈ 2π/√k, critical c ≈ 2√k)
    pitchStiffness: 4,               // lower = laggier nose
    pitchDamping:   2.5,             // under critical → slight overshoot
    rollStiffness:  6,
    rollDamping:    3,
    // Random sway (sum-of-sines pseudo-noise) — purely visual, no flight effect
    swayPitchAmp: 0.6 * DEG2RAD,     // peak pitch sway
    swayRollAmp:  0.9 * DEG2RAD,     // peak roll sway
    swayYawAmp:   0.4 * DEG2RAD,     // peak yaw sway
    swayFrequency: 0.15,             // Hz — base rate (mixed with a 1.7× harmonic)
    cruiseAltAGL: 250,               // m above ground
    minAltAGL: 100,                  // hard floor
    maxAltAGL: 800,                  // ceiling for waypoint variation
  },
};

// ── Enemy Fixed-Wing Failure ──────────────────────────────────────────────
// Tumble parameters for shot-down planes. Unlike the heli, which spins freely
// on all axes, planes keep their nose along velocity and spin around their own
// forward axis — anything from a lazy barrel-roll to a violent spin. Some
// failures add a yaw component on top of the roll for a flat spin.
export const PLANE_FAILURE = {
  rollMin: 0.4,                      // rad/s — minimum roll rate at failure
  rollMax: 6.0,                      // rad/s — maximum roll rate at failure
  rollJitter: 0.25,                  // rad/s² — small chaos torque on top
  flatSpinChance: 0.25,              // probability of flat spin at failure
  flatSpinYawMin: 1.5,               // rad/s — yaw rate range for flat spin
  flatSpinYawMax: 3.5,
  pitchUpDeg: 25,                    // additional nose-up tilt during flat spin
  // Failure forces ramp in over a random window, so a missile hit reads as the
  // pilot losing control rather than the airframe snapping into a spin.
  transitionMin: 1.0,                // s — ramp-in time lower bound
  transitionMax: 3.0,                // s — ramp-in time upper bound
};

// ── Enemy Fixed-Wing ──────────────────────────────────────────────────────
// Per-type kinematic flight params for AI fixed-wing aircraft. Visual pitch is
// the climb angle atan(VR/forwardSpeed); visual roll is the coordinated bank for
// the current yaw rate, atan(V·ω/g). Real MiG-21bis figures where they map
// cleanly, the rest tuned for fun and plausibility.
export const PLANES = {
  mig21: {
    model: 'mig21',
    rwrCode: '21',                    // RWR scope identity — see RWR_DISPLAY
    rwrPriority: 2,
    // The real MiG-21bis cruises ~M0.75 and dashes to M2.05; these cap below the
    // airframe limits so the AI stays predictable against an A-10 (Vmax ~250 m/s).
    cruiseSpeed: 240,                 // m/s — ~Mach 0.75, fast cruise
    attackSpeed: 170,                 // m/s — slow down for guns/IR shot
    breakSpeed: 180,                  // m/s — target through a defensive break. A hard
                                      //   turn bleeds energy; commanding max speed here
                                      //   made the MiG both un-turnable and un-followable.
    extendSpeed: 330,                 // m/s — the run-out after a break. Faster than an
                                      //   A-10 but not by so much that the fight ends.
    maxSpeed: 540,                    // m/s — ~Mach 1.6 (long-range dash only)
    minSpeed: 80,                     // m/s — stall floor
    maxClimbRate: 150,                // m/s — afterburner climb
    maxDescentRate: 120,              // m/s
    forwardAccel: 10,                 // m/s² — full burner. ~1 g of pure thrust; the old
                                      //   22 was over 2 g, which read as teleporting away.
    verticalAccel: 16,                // m/s² — how fast climb/descent rate itself changes
    // Turning is g-limited (see FixedWingFlightModel step 1): ω = g·sqrt(n²−1)/V, so
    // these load factors — not maxYawRate — set the turn rate at combat speeds. At
    // 240 m/s that is 9°/s sustained and 14°/s in a break.
    maxLoadFactor:   4.0,             // g — sustained turn
    breakLoadFactor: 6.0,             // g — instantaneous, defensive break only
    maxYawRate: 16 * DEG2RAD,         // rad/s — control-authority ceiling. Binds only near
                                      //   stall speed, where the g limit alone would let
                                      //   the jet pivot on the spot.
    yawAccel: 15 * DEG2RAD,           // rad/s² — roll-in/roll-out rate; low enough that a
                                      //   turn is visibly entered rather than snapped into
    yawRateGain: 1.0,                 // 1/s — heading-error → desired yaw rate
    // Visual pitch — climb angle + a small body AoA, spring-damped.
    pitchAoADeg: 3.5,                 // degrees of nose-up on top of flight-path angle
    maxPitchDeg: 35,                  // clamp absolute visual pitch
    pitchStiffness: 8,
    pitchDamping:   4,
    // Visual roll — coordinated bank from yaw rate, spring-damped.
    bankPerYawRate: 8.0,              // visual roll = yawRate * this (rad per rad/s)
    maxBankDeg: 80,                   // clamp absolute visual roll
    rollStiffness: 14,
    rollDamping:   4,
    // Subtle sway — fighters are stable but the air is not.
    swayPitchAmp: 0.15 * DEG2RAD,
    swayRollAmp:  0.25 * DEG2RAD,
    swayYawAmp:   0.10 * DEG2RAD,
    swayFrequency: 0.4,               // Hz
    // Cruise envelope (for explore-state waypoint picking)
    cruiseAltAGL: 2500,               // m above ground
    minAltAGL: 200,                   // hard floor
    maxAltAGL: 5000,                  // ceiling for waypoint variation
  },
};

// ── Enemy Ground Vehicles ─────────────────────────────────────────────────
// Per-type kinematic motion params for AI ground vehicles. Y/pitch/roll come
// from per-frame terrain sampling at four contact points, so wheelbase/track
// serve as both the visual footprint and the conformance basis. The AI sets
// only desiredHeading + desiredSpeed.
export const VEHICLES = {
  btr80: {
    model: 'btr80',                  // AssetManager key
    rwrCode: 'V',                    // RWR scope identity — see RWR_DISPLAY. No
    rwrPriority: 0,                  //   radar, so it's a target mark, not a threat.
    cruiseSpeed: 11.176,             // m/s — 25 mph, normal travelling speed
    // Hard cap, deliberately above cruise: this margin is the only authority a
    // follower has to close a gap, since its speed is the leader's plus a
    // correction term and then clamped here. With the two equal a column can
    // never recover ground lost in a turn, and every corner permanently
    // stretches it. Still far under a real BTR-80's ~80 km/h road speed.
    maxSpeed:    14.0,               // m/s
    minSpeed:    0,                  // can stop
    forwardAccel: 2.5,               // m/s² — heavy 8x8 APC, ~5 s to top speed
    brakeDecel:   4.0,               // m/s² — applied when slowing or disabled
    maxYawRate:   25 * DEG2RAD,      // rad/s — pivot rate for an 8-wheel vehicle
    yawAccel:     30 * DEG2RAD,      // rad/s² — angular accel cap
    yawRateGain:  1.2,               // 1/s — heading-error → desired yaw rate
    // Terrain conformance footprint — wheelbase is front-to-back, track
    // left-to-right; yOffset lifts the model origin so wheels don't bury.
    wheelbase: 5.0,
    track:     2.7,
    yOffset:   0.0,
    // Terrain samples are bilinear and noisy at LOD seams, so the visual
    // pitch/roll is spring-damped to keep the body from twitching.
    pitchStiffness: 30, pitchDamping: 9,
    rollStiffness:  30, rollDamping:  9,
    // Bump sway (sum-of-sines pseudo-noise) — purely visual jostle for the
    // surface roughness the heightmap is too coarse to carry. Amplitude and
    // rate both scale with speed fraction, so a stopped vehicle is dead still.
    bumpPitchAmp: 0.7 * DEG2RAD,     // peak pitch sway at top speed
    bumpRollAmp:  1.1 * DEG2RAD,     // peak roll sway (widest — wheels find ruts)
    bumpYawAmp:   0.3 * DEG2RAD,     // peak yaw sway (slight, steering is tracked)
    bumpFrequency: 1.2,              // Hz at top speed (mixed with a 1.7× harmonic)
  },

  // 2K22 Tunguska — stationary radar-guided SAM/AAA vehicle, routed to the
  // EnemyTunguska entity by the `aaa` marker below. Speeds/yaw are pinned to 0
  // so the body never drives or turns; it only conforms to the slope it spawns on.
  tunguska: {
    model: 'tunguska',               // AssetManager key
    rwrCode: 'S6',                   // RWR scope identity — see RWR_DISPLAY
    rwrPriority: 3,                  // radar SAM — outranks everything else airborne
    cruiseSpeed: 0, maxSpeed: 0, minSpeed: 0,
    forwardAccel: 2.0, brakeDecel: 4.0,
    maxYawRate: 0, yawAccel: 1, yawRateGain: 1.0,
    // Terrain conformance footprint (GM-352 chassis is ~7.9 m long, ~3.5 wide).
    wheelbase: 6.0,
    track:     3.2,
    yOffset:   0.0,
    pitchStiffness: 30, pitchDamping: 9,
    rollStiffness:  30, rollDamping:  9,
    // No bump sway block — it never drives, and the sway is speed-scaled anyway.

    // ── AAA behaviour ──────────────────────────────────────────────────────
    aaa: true,                       // route to EnemyTunguska + carry missiles
    turretMesh: 'turret',            // slews to face the player
    radarMesh:  'radar',             // child of the turret; spins continuously
    radarSpinAxis: 'y',              // local axis the antenna sweeps about
    radarSpinRate: 2 * Math.PI,      // rad/s — ~1 revolution per second
    turretSlewRate: 50 * DEG2RAD,   // rad/s — turret traverse rate toward target

    // ── Radar detection ────────────────────────────────────────────────────
    // Drives both the player's RWR (contact/blip/tracking) and this launcher's
    // permission to fire — no contact, no missiles. Gated by range, an elevation
    // cone, and a coarse terrain line-of-sight test.
    radarRange: 27780,               // m (15 NM) — max detection range
    radarMinElevationDeg: 1.0,       // ° — minimum look-up angle; flying below this
                                     //     line slips the player "under the radar"
    radarRayStep: 150,               // m — line-of-sight march step; larger is
                                     //     cheaper but peeks through thin ridges

    missileMagazine: 8,              // rounds carried
    launchElevationDeg: 45,          // ° above horizon for the initial launch (PN guides over after)
    fireRange: 9656,                 // m — 6 statute miles engagement range
    fireInterval: 15.0,              // s — nominal gap between launches
    fireIntervalRandom: 3.0,         // s — ± jitter on the interval
    firstFireDelay: [2.0, 6.0],      // s — random delay before the first launch
    firePuffCount: 8,                // white smoke particles per launch
  },
};

// ── Enemy AI ──────────────────────────────────────────────────────────────
export const ENEMIES = {
  // Takeoff grace period — enemies spawn dormant, holding position, fire and AI
  // until the player has travelled this far (3D) from their own spawn point.
  // Crossing it unpauses everyone at once, so a cold-start mission isn't under
  // attack before the player is airborne.
  activationDistance: 805,           // m (~0.5 mi) from the player's spawn

  terrainQueryInterval: 0.25,        // s — how often AI samples terrain height
  terrainLookaheadSeconds: 3.0,      // s of forward travel to look ahead for hills
  // Extra clearance on top of the altitude an airframe needs to arrest its
  // current sink rate (v²/2a) plus one sample interval of reaction lag. A
  // level-flight floor test is fine at cruise but useless in a 90 m/s dive —
  // this is what lets an evading MiG bottom out its escape without pancaking.
  terrainRecoveryMargin: 60,         // m
  waypointReachRadius: 250,          // m (XZ distance) — counts as "arrived"
  waypointAltJitter: 80,             // m — random ± jitter on cruise alt per waypoint
  altitudeP: 0.05,                   // P-gain on altitude error → vertical rate (1/s)

  // Random hesitation (seconds) before committing to discrete decisions —
  // waypoint reselection, attack engagement — so the AI doesn't look robotic.
  reactionDelay: {
    minDelay: 0.3,
    maxDelay: 1.0,
  },

  // Random-waypoint world bounds (90% of grid extent for margin)
  worldMin: [-32000, -21500],        // [x, z]
  worldMax: [ 32000,  21500],

  // Damage behaviour — per-second decay + degraded handling thresholds.
  damage: {
    decayThreshold: 50,              // health below which damage worsens on its own
    decayRate: 80 / 60,              // hp/sec lost (≈ 80 hp/min)
    liftLossThreshold: 40,           // health below which vertical authority degrades
    liftLossFloor: 0,                // lift multiplier at health = 0 (0 = full loss)
    structuralFailureThreshold: 20,  // health below which AI is bypassed and the wreck tumbles
    failureGravity: 9.81,            // m/s² applied during tumble
    failureAngularImpulse: 0.5,      // rad/s — random initial spin magnitude per axis
    failureAngularJitter: 0.25,      // rad/s² — ongoing chaos torque per axis
    failureSpinChance: 0.65,         // probability the wreck also enters a violent spin
    failureSpinRamp: 0.5,            // rad/s² — yaw-spin acceleration once it triggers
    failureSpinMax: 5.0,             // rad/s — cap on the runaway yaw rate
  },

  // Crash-site fire left where an enemy aircraft hits the ground
  // (fx/GroundFireSystem). Burns for the rest of the scenario with the same
  // rising fire + smoke plume as a destroyed ground vehicle's wreck.
  crashFire: {
    interval: { min: 0.2, max: 0.4 }, // s between puffs (matches the vehicle wreck fire)
    heightOffset: 1.0,                // m above the terrain at the impact point
    scatter: 2.0,                     // m — ± XZ jitter of each puff's origin
  },

  // Fixed-wing attack-on-player state — the pilot works into the tail slot
  // behind the player's velocity vector and slows toward attackSpeed.
  attack: {
    preferredRange: 1800,            // m — sweet spot tail slot behind player
    speedClosureP: 0.08,             // m/s of speed delta per m of distance error
    altitudeP: 0.04,                 // P-gain on alt error → vertical rate (1/s)
    minPlayerSpeed: 30,              // m/s — below this, use heading instead of velocity dir
    interceptLeadSec: 1.0,           // s — small lead, to aim where the player will be

    // ── Flanking approach ──────────────────────────────────────────────────
    // Driving straight down the player's tail makes the run-in a predictable
    // straight line. Instead the pilot aims off to one side of the tail slot
    // (perpendicular to its own bearing, so the track becomes a wide
    // converging arc) and perches above the player. Both offsets fade out
    // with range — full effect at flankBlendFar, gone by flankBlendNear — so
    // the endgame is still a clean tail chase. The plan is re-rolled on a
    // timer, and beyond a few km the lateral offset subtends almost nothing,
    // which is what keeps a 100 km transit from becoming a lazy spiral.
    flankOffset: [1200, 5000],       // m — random lateral aim-off from the slot
    flankAltOffset: [-200, 1000],    // m — random perch above (or below) the player
    flankBlendNear: 2200,            // m — at/inside this range the offsets are gone
    flankBlendFar: 9000,             // m — at/beyond this range they apply in full
    flankRerollInterval: [8, 18],    // s — how long one approach plan is held
    flankSideFlipChance: 0.5,        // probability a reroll swaps left/right
  },

  // Defensive break — when the player settles into the MiG's rear quarter the
  // pilot breaks hard (above the sustained turn rate, nose down for speed),
  // extends to rebuild separation, then re-enters the attack on a fresh
  // flanking plan. Terrain clearance during both phases is handled by the
  // predictive floor (see terrainRecoveryMargin).
  evade: {
    detectRange: 3000,               // m — player must be closer than this to count
    rearConeDeg: 65,                 // ° half-angle off the tail the player must sit in
    noseOnDeg: 35,                   // ° the player's nose must be within, pointed at us
    triggerHold: 1.6,                // s the geometry must persist before breaking
    cooldown: [14, 24],              // s — minimum gap between breaks. This is the main
                                     //     dial on how slippery the MiG feels; too short
                                     //     and it is never in one place long enough to
                                     //     track, let alone shoot

    // A g-limited break covers ~14°/s at combat speed, so these are sweeping
    // turns that take most of their duration to complete — deliberately, since
    // a turn the player can anticipate is a turn they can follow.
    breakTurnDeg: [80, 140],         // ° of heading change commanded in the break
    breakDuration: [5.0, 9.0],       // s the break is held before extending
    breakCompleteDeg: 12,            // ° of remaining heading error that ends it early
    breakDescentRate: 50,            // m/s — nose down through the turn, trading alt for speed
    breakIntoPlayerChance: 0.65,     // probability of turning into the attacker's side
                                     //     (forces an overshoot) rather than away

    extendDuration: [3, 6],          // s of running before re-engaging
    extendRange: 3500,               // m — separation that ends the extend early
    extendDescentRate: 25,           // m/s — shallow descent while accelerating away
    extendMinAltAGL: 600,            // m — level off here rather than keep sinking
    extendMaxTurnDeg: 90,            // ° — if running straight away needs more turn than
                                     //     this, hold the current heading instead of
                                     //     handing the player our tail a second time
  },

  // Formation (trail/column) — followers tail the leader along its heading.
  formation: {
    spacing: 80,                     // m — nominal gap between successive birds
    spacingJitter: 15,               // m — ± random variation on longitudinal gap
    lateralJitter: 20,               // m — ± random side-stagger from centerline
    altitudeJitter: 8,               // m — ± random altitude offset per follower
    distanceP: 0.08,                 // P-gain on distance error → speed delta (1/s)
    maxSpeedDelta: 15,               // m/s — clamp on speed correction vs cruise
  },

  // Ground-vehicle formation — tighter than the air formation and with no
  // altitude term (AIDriver._tickFollow ignores it), since tank columns read
  // better with short gaps and little lateral jitter.
  //
  // Unlike the air formation, followers do not hold a slot in the leader's
  // current frame — they chase a breadcrumb trail of where the leader actually
  // drove. A slot offset backwards along the leader's *present* heading is
  // wrong the moment the leader turns: the whole column swings onto the new
  // heading at once and cuts the corner across open ground, which on a road
  // network means leaving the road at every junction. Trailing the recorded
  // line makes each vehicle round the corner where the one ahead of it did.
  vehicleFormation: {
    spacing: 25,
    spacingJitter: 4,
    lateralJitter: 2,
    distanceP: 0.3,
    maxSpeedDelta: 4,

    // Breadcrumb recording. Every driver lays a trail, because formations are
    // chains (each vehicle follows the one ahead, not all following the head),
    // so all but the last are somebody's leader. The buffer only has to span
    // one gap — spacing + jitter — but is sized well past that so a follower
    // that falls behind still has a line to rejoin.
    trailSampleDist: 3,              // m between recorded points
    trailLength: 150,                // m of history retained

    // How far up the trail a follower aims, relative to its own position on it.
    // Clamped so the aim point never passes the nominal slot, so on-station
    // behaviour is unchanged; it only engages when a vehicle has fallen behind,
    // where aiming at the distant slot would cut every corner between here and
    // there. Roughly a second and a half of travel.
    followLookahead: 12,             // m

    // ± correction band applied to the *integrated* along-trail position, not a
    // search around last frame's answer. Kept tight because that integration is
    // the thing keeping a follower on the right branch where the trail folds
    // back on itself — a wide band lets the geometry drag the estimate onto the
    // returning leg of a U-turn, where the follower reads itself as arrived.
    followProjectWindow: 8,          // m
  },

};

// ── Rendering ──────────────────────────────────────────────────────────────
export const RENDERING = {
  pixelRatioMultiplier: 0.5,
  anisotropyCap: 16,
  nearPlane: 0.165,
  farPlane: TERRAIN.viewDistance,
  shadow: {
    mapSize: 2048,
    frustumSize: 10,       // meters — half-extent of the shadow camera box around the aircraft
    near: 5,
    far: 1000,
    bias: -0.0005,
    normalBias: 0.02,
  },
  shadowCockpit: {
    frustumSize: 2,        // meters — tighter frustum for cockpit-only shadows
    near: 1,
    far: 40,
  },
};

// ── Film look ───────────────────────────────────────────────────────────────
// A Lightroom Classic / Camera Raw preset applied as a final grade, baked to a
// 3D LUT by fx/FilmLookPass. The preset itself lives in fx/presets/ (generated
// from its .xmp by tools/xmp-to-preset.mjs); this block is only the plumbing.
//
// It is scenario-independent on purpose: it is a look for the whole sim, not a
// weather condition, so unlike bloom/contrast/grain it takes no per-LIGHTING
// override. It is not player-facing either: `SETTINGS.filmLook` is the one
// strength the sim ships with, read once when the pass is built.
export const FILM_LOOK = {
  // Lattice edge length. 33 is the .cube convention and plenty for a smooth
  // grade; the cost is build time (~15 ms) and 287 KB of VRAM, both one-off.
  lutSize: 33,

  // Camera Raw settings layered over the preset before it is compiled, in the
  // same `crs:` vocabulary. This is where to taste-tune the look: the preset
  // module under fx/presets/ is *generated* from its .xmp and a hand-edit there
  // is lost the next time `npm run build:preset` runs.
  //
  // Portra is a deliberately muted stock and this grade keeps under half the
  // original saturation, so these two are the dials that usually want moving.
  // Both are real Camera Raw stages the grade already implements, scored here
  // on how much of the ungraded saturation survives across a scene palette:
  //
  //        +0     +10    +20    +30
  //   Sat  47%    51%    56%    60%
  //
  // Vibrance reaches a similar average but spends it differently — it is
  // weighted by how unsaturated a colour already is, so haze and terrain come
  // up while explosions and fire stay put. Saturation lifts everything equally
  // and will push fire toward neon first.
  adjust: {
    Saturation: 0,   // Camera Raw global saturation, -100..100
    Vibrance: 15,     // ditto, but protects already-saturated colours
  },
};

// ── Stars ───────────────────────────────────────────────────────────────────
// Real star positions from the Yale Bright Star Catalog, drawn by
// world/StarField.js as point sprites over a starless background plate. This
// block holds the catalogue, sky geometry and magnitude response shared by all
// scenarios; per-condition overrides live in each LIGHTING entry's `stars`
// block, and a condition with no `stars` block leaves the field switched off —
// which is how every daylight scenario opts out.
export const STARS = {
  // Packed catalogue, built by tools/build-stars.mjs. 9,096 stars, 53 KB.
  data: 'data/stars.bin',

  // Sphere radius in metres. Must sit inside the far plane (TERRAIN.viewDistance)
  // while still being further than any terrain that could draw against it —
  // stars test depth but never write it, so anything nearer occludes them. The
  // tallest terrain is heightScale ≈ 900 m, which subtends under 3° at 20 km, so
  // the horizon fade below extinguishes stars long before a ridge can reach one.
  radius: 39000,

  // ── Sky geometry ─────────────────────────────────────────────────────────
  // Which stars are up, and where. Latitude sets how high the pole sits (Polaris
  // rides at exactly the observer's latitude) and how the sky tilts; sidereal
  // time is the right ascension crossing the meridian, so it alone picks what is
  // on show. 40°N at 5.5h puts Orion due south and high, with Sirius, Taurus,
  // the Pleiades, Auriga and Gemini around it and the Dipper swinging northeast
  // — the most recognisable sky there is, and the one the Milky Way runs through.
  latitude: 40,        // degrees north
  siderealTime: 5.5,   // hours
  northAzimuth: 0,     // degrees — spins the whole sky about the vertical

  // ── Magnitude response ───────────────────────────────────────────────────
  // 6.5 is the naked-eye limit and the depth the catalogue is complete to.
  // Lowering it thins the field (and is free — it's a draw-range prefix).
  magnitudeLimit: 6.5,

  // The magnitude that renders at unit flux; everything else is Pogson-scaled
  // off it. Effectively the field's exposure — lower to brighten the whole sky.
  magnitudeRef: 1.0,

  // Sprite size in CSS pixels. Flux buys area on a curve far shallower than its
  // 25-stop range, so faint stars stay resolvable and bright ones stay stars
  // rather than discs. Sirius clamps at the 5 px ceiling, a 6th-magnitude star
  // lands near 2. The floor matters more than it looks: below ~1.5 px a sprite
  // can fall between fragment centres, and faint stars flicker as they drift.
  sizeMin: 1.5,
  sizeMax: 4.0,
  sizeScale: 1.5,
  sizeGamma: 0.33,

  // Intensity, in linear HDR. With the night condition's 0.62 bloom threshold,
  // roughly the top two magnitudes glow on their own — which is what sells the
  // bright ones as bright rather than just large.
  brightness: 2.0,
  brightnessGamma: 0.5,

  // Gaussian falloff of the sprite core; higher is tighter and more point-like.
  // Keep it gentle: these sprites are only a few pixels across, so a tight
  // profile means no fragment centre ever lands near the peak and most of the
  // star's energy is thrown away before it reaches the framebuffer.
  coreSharpness: 2.2,

  // How much of each star's B−V hue survives. Scotopic vision is nearly
  // colourblind, so real stars look far whiter than their spectra — 1 gives
  // full-strength blues and oranges, 0 a monochrome field.
  saturation: 0.5,

  // Scintillation. Depth of the shimmer, its rate, and the extra gain applied
  // at the horizon, where the slant path through the atmosphere is longest.
  twinkle: 0.3,
  twinkleSpeed: 2.5,
  twinkleHorizon: 2.5,

  // Atmospheric extinction — stars fade out into the horizon murk between these
  // altitudes. The floor sits slightly below level so the horizon still dims
  // properly from altitude, where it drops a couple of degrees below the eye.
  fadeLowDeg: -2,
  fadeHighDeg: 7,
};

// ── Volumetric Clouds ───────────────────────────────────────────────────────
// A single horizontal cloud deck raymarched by fx/CloudLayer.js as a composer
// pass. This block holds the structural / performance / animation parameters
// shared across all scenarios; the per-scenario *look* (coverage, density,
// albedo, sun response, optional altitude override) lives in each LIGHTING
// entry's `clouds` block above and is merged in at applyLighting time. A
// condition with no `clouds` block (or coverage 0) disables the pass entirely.
export const CLOUDS = {
  enabled: true,

  // Raymarch resolution as a fraction of the framebuffer, Catmull-Rom upscaled
  // at composite time — the single biggest performance lever. Temporal
  // accumulation carries the smoothness, so fractional values still read clean
  // (0.25 is viable; 0.5 trades cost for crisper detail).
  resolutionScale: 1,

  // Temporal reprojection — EMA weight of the current frame's sample blended
  // into the reprojected history, so ~1/0.05 ≈ 20 effective frames. Lower is
  // smoother but slower to react (more ghosting); higher is crisper but grainier.
  temporalBlend: 0.05,

  // Separable depth-aware blur over the accumulated cloud buffer, run between
  // the raymarch and the composite (cloud_blur_frag.glsl). This is the denoiser
  // the temporal pass can't be: accumulation has to snap to the raw sample
  // wherever reprojected history disagrees, or stale history smears, leaving
  // those pixels at full single-frame march grain — and a blur doesn't care
  // where the grain came from. It runs on a copy rather than the history
  // buffer, so it can't feed back and dissolve the deck over successive frames.
  //
  // Every cloud pixel is blurred equally; only the OCCLUSION mask is preserved,
  // since taps are gated by scene depth and cloud never bleeds across the
  // airframe or a ridgeline. Cloud-against-sky edges do soften — that's the
  // trade a uniform blur makes.
  blur: {
    // Compiled out entirely when false — no passes, no extra render targets.
    enabled: true,

    // Gaussian sigma in cloud texels, the main strength knob. ~1 is subtle, ~2.5
    // clearly soft, past ~4 the deck goes gauzy and loses its silhouette.
    sigma: 3,

    // Taps per side, per pass. The kernel truncates here, so this caps how far
    // sigma can reach — keep it near 2× sigma, or raising sigma just clips the
    // tail. Shader #define (needs a reload); costs 2×taps fetches per pass.
    taps: 5,

    // How close in depth a tap must be to contribute, in raw log-depth units.
    // Log depth makes this scale-invariant: ~0.01 is about a 10% difference at
    // any range, passing neighbouring cloud and rejecting nearer geometry. Lower
    // it if cloud creeps over silhouettes, raise it if terrain edges stay grainy.
    depthTolerance: 0.01,
  },

  // Primary raymarch step budget through the slab (shader #define). Step size
  // adapts to the traversed slab thickness, so cost per cloud pixel is fixed
  // regardless of view angle.
  maxSteps: 16,

  // FBM octaves in the density field (#define). Each adds finer wisps at roughly
  // half the cost of the last.
  octaves: 5,

  // Master scale on the direct (sunlit-face) term, applied on top of each
  // condition's `sunScatter`. 0 removes direct sun entirely, leaving ambient.
  // Pull this when sunlit tops read too bright across the board.
  sunStrength: 0.5,

  // Default layer altitude band (metres ASL). Terrain tops out ~900 m, so a base
  // well above that keeps the low country clear until the player climbs into the
  // deck. Overridable via LIGHTING[x].clouds.altitude.
  altitude: { base: 1750, top: 3500 },

  // Horizontal reach (metres), just past TERRAIN.viewDistance so the deck fills
  // the sky to the horizon and fades into fog. Rays never march beyond this.
  drawDistance: 44000,

  // Metres per wrap of the base FBM octave — the scale of the broad cloud
  // masses. Larger = bigger, sparser systems. Per-scenario overridable.
  noiseScale: 3600,

  // Deck drift — direction is normalized, speed in m/s. Animated over time, so
  // the field slowly evolves as it slides across the sky.
  windDir: [1.0, 0.0, 0.35],
  windSpeed: 6,

  // Metres over which the deck fades in near the camera (keyed to mean cloud
  // distance), so flying through a cloud face doesn't pop. 0 disables.
  nearFade: 0,

  // ── Fine wisp detail ──────────────────────────────────────────────────────
  // A second, much finer noise field that ERODES the base mass rather than
  // adding to it, giving the deck torn edges, filaments and pinholes at a size
  // set here in absolute metres — decoupled from `noiseScale`, which only sizes
  // the broad systems. Cheap by construction: sampled only where base density
  // falls strictly between 0 and 1 (the coverage feather), never for the sun
  // self-shadow tap (so wisps stay lit like the cloud they hang off instead of
  // going dark as they thin), and faded out at range, past which the march steps
  // are longer than the wisps and sampling them only buys aliasing. Terrain
  // cloud shadows ignore wisps entirely — invisible at shadow scale, and they'd
  // cost a fetch on every ground fragment. `enabled: false` compiles the path
  // out of the shader.
  wisp: {
    enabled: true,
    // Master erosion depth, 0..1. ~0.3 frays edges lightly; 0.6+ shreds them
    // into filaments and starts eating coverage (raise the condition's
    // `coverage` to compensate). Scaled per condition by `wispStrength`.
    strength: 0.5,
    // Metres per wrap of the FIRST wisp octave — the coarsest wisp size, held
    // absolute so wisps keep their apparent size however `noiseScale` moves.
    // 150–400 reads as torn cumulus edges; below ~80 it just shimmers.
    scale: 220,
    // Wisp FBM octaves (shader #define). Each is ~2.6× finer and costs one
    // fetch on eroded samples; 2 is plenty.
    octaves: 2,
    // Wisp drift as a multiple of `windSpeed`. >1 shears the detail through the
    // mass, so edges churn and boil rather than sliding along rigidly.
    drift: 1.6,
    // Metres over which wisps fade with distance — full detail to `fadeStart`,
    // gone by `fadeEnd`.
    fadeStart: 8000,
    fadeEnd: 26000,
  },

  // Soft cloud shadows on the terrain. Terrain materials sample a trimmed copy
  // of the same density field (fewer octaves/steps, since shadows are
  // low-frequency) along the sun ray, so the pattern tracks and drifts with the
  // visible deck. Costs a handful of texture reads per terrain fragment.
  shadow: {
    enabled: true,
    // Maximum darkening under fully opaque cloud (1 = direct + ambient fully
    // extinguished in the umbra), scaling smoothly from 0 in clear sky.
    strength: 0.8,
    // Fattens or thins the shadow footprint WITHOUT touching the visible clouds,
    // by biasing coverage for the shadow's density threshold only. Effective
    // coverage is clamped to [0,1], so 0 = shadows match the clouds overhead.
    coverageBias: 0,
    octaves: 3,   // FBM octaves in the shadow density (#define) — only broad masses read
    steps: 4,     // density samples along the sun ray (#define); the field is smooth here
  },

  // ── Quality presets ───────────────────────────────────────────────────────
  // The player-facing Low/Medium/High setting (SETTINGS.cloudQuality). The
  // fourth choice, 'off', has no entry here: it isn't a sampling level but a
  // master switch (CloudLayer.setEnabled), so there is no preset to write back.
  // These are
  // pure-cost knobs — none of them change the *look* of the weather, only how
  // finely it's sampled. App.setCloudQuality writes the chosen preset back into
  // this block (so anything built later starts at the right quality) before
  // pushing it into CloudLayer/Terrain live, and applies the default at startup,
  // so these are the values that actually reach the GPU.
  //
  //   resolutionScale — raymarch pixels (the biggest lever)
  //   blurSigma       — blur.sigma; more denoise for coarser marches
  //   maxSteps        — primary march steps                (shader #define)
  //   octaves         — density FBM octaves                (shader #define)
  //   shadowSteps     — shadow.steps, per terrain fragment (shader #define)
  quality: {
    // Half-res march with a short step budget and broad-only detail, leaning on
    // the strongest blur to cover the extra per-sample grain.
    low:    { resolutionScale: 0.5, blurSigma: 3, maxSteps: 8, octaves: 3, shadowSteps: 3 },
    medium: { resolutionScale: 1.0, blurSigma: 2, maxSteps: 16, octaves: 5, shadowSteps: 4 },
    // Full-res with ~2× the steps: the march is smoother on its own, so the blur
    // can stay light and let the extra octave's detail survive to the screen.
    high:   { resolutionScale: 1.0, blurSigma: 2, maxSteps: 24, octaves: 6, shadowSteps: 6 },
  },
};

// ── User settings ───────────────────────────────────────────────────────────
// Defaults for the in-game settings overlay. App copies them into a live
// `settings` store, and the overlay reads them for its initial control state.
// The player-facing values persist across sessions in localStorage (see
// `core/UserSettings.js`), which layers the saved values over these defaults —
// so these are the "fresh install" state and the target of a settings reset.
export const SETTINGS = {
  hud: true,                    // show GameHUD + CommandFeedback
  mouseSensitivity: 1.0,        // multiplier on AUTOPILOT.sensitivity
  mouseSensitivityMin: 0.25,
  mouseSensitivityMax: 3.0,
  mouseSensitivityStep: 0.05,
  controlScheme: 'basic',       // see input/ControlScheme.js
  controlSchemeOptions: ['basic', 'keyboard', 'mouse', 'gamepad', 'joystick'],
  invertPitch: false,           // manual mouse stick + gamepad left stick
  stickDeadzone: 0.08,          // gamepad / joystick, radial, fraction of full travel
  stickDeadzoneMin: 0,
  stickDeadzoneMax: 0.3,
  stickDeadzoneStep: 0.01,
  joystickAxes: null,           // player-assigned joystick axes (DeviceInput layout), null = browser default
  masterVolume: 1.0,            // user master volume (0–1), multiplies the audio master gain
  musicVolume: 1.0,             // menu/victory music (0–1), on top of the master volume
  jtacVolume: 1.0,              // mission radio — tower + JTAC (0–1), on top of the master volume
  voiceVolume: 1.0,             // flight-school instructor VO (0–1), on top of the master volume
  volumeStep: 0.05,             // step shared by every volume slider
  renderScale: 1.0,             // multiplier on devicePixelRatio (renderer pixel ratio)
  renderScaleOptions: [0.5, 1.0, 2.0],
  postProcessing: true,         // bloom + contrast + film-grain passes
  antialiasing: 'msaa',         // 'off' | 'msaa' (only meaningful when post-processing is on)
  antialiasingOptions: ['off', 'msaa'],
  shadowResolution: 2048,       // sunLight shadow map size (px) — matches RENDERING.shadow.mapSize default
  shadowResolutionOptions: [1024, 2048, 4096],
  cloudQuality: 'medium',       // 'off', or a sampling preset — key into CLOUDS.quality
  cloudQualityOptions: ['off', 'low', 'medium', 'high'],
  // Film-emulation grade strength (0 = pass disabled). Not in the overlay and
  // not persisted — a build constant that App reads when it builds the pass.
  filmLook: 1.0,
};
